import { canMutateOnline } from '@/services/connectivityState.js';
import { getOfflineCacheOwnerId, getOfflineCacheWriteGeneration, rotateOfflineCacheWriteGeneration } from '@/services/offlineIdentity.js';
import apiClient from '@/utils/axios.js';
import { matchesAllTokens, normalizeTerm, selectDriverToken, tokenizeName, tokenizeTerm } from '@/utils/searchTokens.js';
import Dexie from 'dexie';

const offlineDB = new Dexie('InventreeDB');

/** Prefer confirmed connectivity; fall back to navigator only before service init. */
const isConfirmedOnline = () => {
    try {
        if (canMutateOnline()) return true;
        // While still 'checking', do not treat as online for network pulls
        return false;
    } catch {
        return typeof navigator !== 'undefined' ? navigator.onLine : false;
    }
};

// Every authoritative network cache write captures this epoch before its
// request starts. Logout/cache-clear increments it synchronously, preventing a
// late response from a previous identity from repopulating IndexedDB.
let authoritativeCacheEpoch = 0;

export const invalidateOfflineCacheWrites = ({ persist = true } = {}) => {
    authoritativeCacheEpoch += 1;
    return persist ? rotateOfflineCacheWriteGeneration() : getOfflineCacheWriteGeneration();
};

const captureOfflineCacheEpoch = ({ requireOwner = false } = {}) => ({
    epoch: authoritativeCacheEpoch,
    generation: getOfflineCacheWriteGeneration(),
    ownerId: getOfflineCacheOwnerId(),
    requireOwner,
});
export const captureOfflineCacheWriteEpoch = captureOfflineCacheEpoch;
const isOfflineCacheEpochCurrent = (snapshot) => {
    // Backward compatibility for callers/tests that captured the old numeric
    // module-local epoch before this context became owner-aware.
    if (typeof snapshot === 'number') return snapshot === authoritativeCacheEpoch;
    if (!snapshot || typeof snapshot !== 'object') return false;

    const currentOwnerId = getOfflineCacheOwnerId();
    if (snapshot.requireOwner && (snapshot.ownerId === null || snapshot.ownerId === undefined || snapshot.ownerId === '')) {
        return false;
    }

    return (
        snapshot.epoch === authoritativeCacheEpoch &&
        snapshot.generation === getOfflineCacheWriteGeneration() &&
        String(snapshot.ownerId ?? '') === String(currentOwnerId ?? '')
    );
};

class StaleOfflineCacheWriteError extends Error {
    constructor() {
        super('Offline cache session changed before the response was stored.');
        this.name = 'StaleOfflineCacheWriteError';
    }
}

const assertOfflineCacheEpoch = (epoch) => {
    if (!isOfflineCacheEpochCurrent(epoch)) {
        throw new StaleOfflineCacheWriteError();
    }
};

const isStaleOfflineCacheWrite = (error) => error?.name === 'StaleOfflineCacheWriteError';

/**
 * Publish one authoritative network result only if the cache session/owner is
 * still the one captured before the request began. The final assertion runs
 * inside the Dexie transaction, so a cross-tab invalidation rolls the write
 * back even when it lands while IndexedDB is yielding.
 *
 * Modes:
 * - put / bulkPut — upsert only (safe for partial pages)
 * - bulkReplace — upsert + delete local rows missing from a full authoritative set
 *   (optional scopeFilter limits which local rows may be deleted)
 */
export const writeAuthoritativeCache = async (
    tableName,
    value,
    { mode = Array.isArray(value) ? 'bulkPut' : 'put', snapshot = captureOfflineCacheEpoch({ requireOwner: true }), scopeFilter = null } = {},
) => {
    const table = offlineDB[tableName];
    if (!table || !['put', 'bulkPut', 'bulkReplace'].includes(mode)) {
        throw new Error(`Unsupported authoritative cache write for table "${String(tableName)}".`);
    }

    assertOfflineCacheEpoch(snapshot);
    return offlineDB.transaction('rw', table, async () => {
        assertOfflineCacheEpoch(snapshot);

        if (mode === 'put') {
            const result = await table.put(value);
            assertOfflineCacheEpoch(snapshot);
            return result;
        }

        const rows = Array.isArray(value) ? value : [];
        if (rows.length) {
            await table.bulkPut(rows);
        }

        if (mode === 'bulkReplace') {
            const existing = scopeFilter ? await table.filter(scopeFilter).toArray() : await table.toArray();
            const incomingIds = new Set(rows.filter((row) => row?.id != null).map((row) => String(row.id)));
            const staleIds = existing.filter((row) => row?.id != null && !incomingIds.has(String(row.id))).map((row) => row.id);
            if (staleIds.length) {
                await table.bulkDelete(staleIds);
            }
        }

        assertOfflineCacheEpoch(snapshot);
        return rows.length;
    });
};

offlineDB.version(20).stores({
    auth: 'id',
    units: 'id,name',
    sizes: 'id,name',
    categories: 'id,name,type,status',
    products: 'id,name,categories_name',
    product_variants: 'id,product_id,name',
    types: 'id,name',
    materials: 'id,name',
    colors: 'id,name',

    sale_drafts: 'tabId',
    pending_sales: '++id',

    branch_products: 'key, branch_id, variant_id, real_qty, reserved_qty, updated_at, product_name, variant_name, sales_price, tax ',

    sale_details: 'sales_order, id, date, status, payment_status, branch_id, branch_name, customer_phone, customer_name',
    sale_items: '[sales_order+id], sales_order, product_name',
    sale_payments: '[sales_order+id], sales_order, payment_method, status',
    refunds: '++id, refund_number, sale_id, user_id, created_at',
});

offlineDB.version(21).stores({
    return_branches: 'id,name',
});

/**
 * v22 — four-domain offline lists/details, sync metadata, corrected branch_products indexes,
 * drop obsolete pending_sales (read-only offline policy; no mutation outbox).
 * Do not edit v20/v21 in place. Protected data (auth, sale_drafts, reservations) survives upgrade.
 */
offlineDB.version(22).stores({
    branches: 'id, name, status, city, country',
    suppliers: 'id, name, email, phone, company_name, status',
    customers: 'id, name, phone, email, type, wholesaler_id, company_name, status',
    purchases: 'id, purchase_order, invoice_number, supplier_id, status, payment_status, date',

    branch_details: 'id',
    supplier_details: 'id',
    customer_details: 'id',
    purchase_details: 'id',

    offline_sync_meta: 'key, table, scope, last_success_at',

    // Corrected indexes (drops invalid trailing-space `tax ` from v20)
    branch_products: 'key, branch_id, [branch_id+variant_id]',

    pending_sales: null,
});

/**
 * v23 — Sales, Transfers, Gate passes, Wholesaler order requests (list + detail).
 * Bounded list retention for sales/transfers/gatepasses is enforced by offlineSyncService.
 */
offlineDB.version(23).stores({
    sales: 'id, sales_order, status, payment_status, date, branch_id, customer_id, created_at',
    transfers: 'id, transfer_number, status, from_branch_id, to_branch_id, created_at',
    gate_passes: 'id, gatepass_no, status, date, sale_id, transfer_id',
    wholesaler_order_requests: 'id, order_no, status, wholesaler_id, wholesaler_name, sale_id, created_at',

    sales_details: 'id',
    transfer_details: 'id',
    gate_pass_details: 'id, gatepass_no',
    order_request_details: 'id',
});

/**
 * v24 — multi-branch sale allocations + aggregate availability cache.
 * Do not edit v20–v23 in place. Protected data survives upgrade.
 * variant_availability is a display cache only and must never authorise a sale.
 */
offlineDB.version(24).stores({
    sales: 'id, sales_order, status, payment_status, date, branch_id, payment_branch_id, customer_id, created_at',
    sale_allocations: 'id, sale_id, branch_id, [sale_id+branch_id]',
    variant_availability: 'key, variant_id',
});

/**
 * v25 — expenses (bounded list + detail) and accounts (full lean master + identity detail).
 * Account ledger is never cached; current_balance is a stamped snapshot only.
 */
offlineDB.version(25).stores({
    expenses: 'id, expense_number, date, branch_id, payment_method, category_id, created_at',
    expense_details: 'id',
    accounts: 'id, account_title, status, bank_name',
    account_details: 'id',
});

/**
 * v26 — index branch capability flags used by POS/purchase/transfer pickers.
 * Do not edit v20–v25 in place.
 */
offlineDB.version(26).stores({
    branches: 'id, name, status, city, country, holds_stock, can_originate_sales, can_originate_purchases, can_receive_stock',
});

/**
 * v27 — POS search catalogue.
 *
 * Branch-agnostic on purpose: IndexedDB forbids a multiEntry index on a compound
 * key path, so `[branch_id+*name_tokens]` is not expressible. Search resolves
 * candidate variant ids here, then joins quantities from branch_products via its
 * existing [branch_id+variant_id] index. branch_products remains the sole
 * authority for stock and branch-resolved price/tax/discount.
 *
 * Deliberately NOT the existing product_variants store: the admin datatable
 * bulkPuts admin-shaped rows into that one, which would strip name_tokens and
 * silently drop variants out of POS search.
 */
offlineDB.version(27).stores({
    pos_catalog: 'id, name_lower, sku_lower, code_lower, *name_tokens, _cached_at',
});

/**
 * v28 — FBR buyer classification on cached customers.
 *
 * Indexed so the customers datatable can filter offline by buyer type the same
 * way it already filters by `type`. The advance-tax *rate* is deliberately not
 * cached: it is resolved server-side from configuration at checkout, and a
 * stale cached rate would quote a total the server then rejects.
 */
offlineDB.version(28).stores({
    customers: 'id, name, phone, email, type, buyer_type, wholesaler_id, company_name, status',
});

/**
 * v29 — customer-owned automotives (the Automotive addon).
 *
 * is_archived / service_status / expiry_status are indexed because the offline
 * DataTable resolves filters against literal row values via getRawCellValue —
 * anything the server computes on the fly would silently match nothing offline.
 * They are stored as real columns server-side for the same reason.
 *
 * customer_id MUST be written as a Number. Dexie is type-strict, so a row saved
 * with '5' is invisible to .where('customer_id').equals(5), which would leave
 * the POS picker permanently empty for that customer.
 */
offlineDB.version(29).stores({
    automotives:
        'id, customer_id, registration_number, serial_number, category, is_archived, service_status, expiry_status, next_service_due_at, next_expiry_at, updated_at',
    automotive_details: 'id',
});

/**
 * v30 — load form delivery runs.
 *
 * Read-only like every other server domain: dispatch and reconcile are
 * mutations and are gated by the axios policy and `meta.requiresOnline`. The
 * lean list carries what the DataTable filters on; the detail store holds the
 * visited run with its per-invoice lines.
 *
 * branch_id and delivery_person_id MUST be written as Numbers — Dexie is
 * type-strict and a row saved with '5' is invisible to .equals(5).
 */
offlineDB.version(30).stores({
    load_forms: 'id, load_form_no, status, date, branch_id, delivery_person_id, updated_at',
    load_form_details: 'id, load_form_no',
});

/**
 * v31 — the delivery-people directory.
 *
 * Master data, not a document stream, so the whole list is cached rather than a
 * recent window: a driver who has not run in months is still someone a gate pass
 * or load form names, and the offline list is the only place that name resolves.
 *
 * is_active is stored as 1/0 and never true/false. The offline DataTable
 * compares the stringified row value against the filter's literal '1'/'0', so a
 * boolean would stringify to 'true' and match neither option.
 *
 * branch_id MUST be written as a Number — Dexie is type-strict and a row saved
 * with '5' is invisible to .where('branch_id').equals(5).
 */
offlineDB.version(31).stores({
    delivery_persons: 'id, name, phone, cnic, vehicle_no, licence_expiry, branch_id, is_active, updated_at',
    delivery_person_details: 'id',
});

// v32 — real LRU for the demand-cached tier. `_accessed_at` is stamped on read
// (getDomainDetail) so eviction can order by use rather than by write time, and
// `_pinned` keeps "Make available offline" records out of reach of quota
// pressure. Both are declared as indexes so a future eviction pass can range-scan
// them; today's pass still reads the table, because a Dexie index omits rows whose
// key is undefined and those are exactly the pre-v32 rows it has to fall back for.
offlineDB.version(32).stores({
    pos_catalog: 'id, name_lower, sku_lower, code_lower, *name_tokens, _cached_at, _accessed_at, _pinned',
    branch_details: 'id, _accessed_at, _pinned',
    supplier_details: 'id, _accessed_at, _pinned',
    customer_details: 'id, _accessed_at, _pinned',
    purchase_details: 'id, _accessed_at, _pinned',
    sales_details: 'id, _accessed_at, _pinned',
    transfer_details: 'id, _accessed_at, _pinned',
    gate_pass_details: 'id, _accessed_at, _pinned',
    order_request_details: 'id, _accessed_at, _pinned',
    expense_details: 'id, _accessed_at, _pinned',
    account_details: 'id, _accessed_at, _pinned',
    automotive_details: 'id, _accessed_at, _pinned',
    load_form_details: 'id, _accessed_at, _pinned',
    delivery_person_details: 'id, _accessed_at, _pinned',
});

export const getReturnBranchesFromIDB = async () => offlineDB.return_branches.orderBy('name').toArray();

export const syncReturnBranchesToIDB = async () => {
    if (!isConfirmedOnline()) return [];
    const cacheEpoch = captureOfflineCacheEpoch();
    const res = await apiClient.get('/refund/branch/getAllowedReturnBranches', { offlinePolicy: 'read' });
    const branches = res.data?.data ?? [];
    assertOfflineCacheEpoch(cacheEpoch);
    await offlineDB.transaction('rw', offlineDB.return_branches, async () => {
        assertOfflineCacheEpoch(cacheEpoch);
        await offlineDB.return_branches.clear();
        if (branches.length) await offlineDB.return_branches.bulkPut(branches);
    });
    return branches;
};

const normalizeSalePayload = async (sale) => {
    if (!sale) return null;

    const items = await offlineDB.sale_items.where('sales_order').equals(sale.sales_order).toArray();
    const payments = await offlineDB.sale_payments.where('sales_order').equals(sale.sales_order).toArray();
    return {
        sale: {
            id: sale.id,
            sales_order: sale.sales_order,
            date: sale.date,
            created_at: sale.created_at ?? sale.date,
            status: sale.status,
            payment_status: sale.payment_status,
            branch_id: sale.branch_id,
            user_id: sale.user_id ?? sale.cashier_id ?? null,
            fully_refunded: sale.fully_refunded ?? false,
            refunded_amount: sale.refunded_amount ?? 0,
            refunded_qty: sale.refunded_qty ?? 0,
        },
        branch: {
            id: sale.branch_id,
            name: sale.branch_name,
        },
        customer: {
            id: sale.customer_id,
            name: sale.customer_name,
            phone: sale.customer_phone,
            wallet_balance: sale.customer_wallet_balance ?? 0,
            wallet_status: sale.customer_wallet_status ?? 'active',
            type: sale.customer_type ?? null,
        },
        cashier: {
            id: sale.cashier_id ?? sale.user_id ?? null,
            name: sale.cashier_name,
        },
        items: items.map(({ sales_order, ...rest }) => rest),
        summary: {
            gross_amount: sale.gross_amount,
            discount: sale.discount,
            tax: sale.tax,
            fed_tax: sale.fed_tax ?? 0,
            extra_tax: sale.extra_tax ?? 0,
            final_total: sale.final_total,
            paid_amount: sale.paid_amount,
            balance_due: sale.balance_due,
        },
        payments: payments.map(({ sales_order, ...rest }) => rest),
    };
};

export const cacheSaleToIDB = async (saleNumber) => {
    if (!saleNumber || !isConfirmedOnline()) return { status: 'error', message: 'No connection or sale number.' };
    const cacheEpoch = captureOfflineCacheEpoch();

    try {
        const res = await apiClient.post(
            '/refund/searchSale',
            {
                sale_number: saleNumber,
            },
            { offlinePolicy: 'read' },
        );

        const payload = res?.data?.data;
        if (!payload) {
            return { status: 'error', message: 'Sale not found on server.' };
        }

        assertOfflineCacheEpoch(cacheEpoch);
        await saveSaleFromApi(payload, cacheEpoch);

        return { status: 'success', data: payload };
    } catch (err) {
        if (isStaleOfflineCacheWrite(err)) {
            return {
                status: 'error',
                message: 'The offline cache session changed before the sale could be stored.',
            };
        }

        return {
            status: 'error',
            message: err.response?.data?.message || 'Failed to cache sale.',
        };
    }
};
const normalizeRefundSaleCacheRecords = (payload) => {
    const salesOrder = payload?.sale?.sales_order;
    if (!salesOrder) return null;

    return {
        salesOrder,
        detail: {
            sales_order: salesOrder,
            id: payload.sale.id,
            date: payload.sale.date,
            created_at: payload.sale.created_at ?? payload.sale.date,
            status: payload.sale.status,
            payment_status: payload.sale.payment_status,
            user_id: payload.sale.user_id ?? payload.cashier?.id ?? null,
            fully_refunded: payload.sale.fully_refunded ?? false,
            refunded_amount: payload.sale.refunded_amount ?? 0,
            refunded_qty: payload.sale.refunded_qty ?? 0,

            branch_id: payload.sale.branch_id ?? payload.branch?.id ?? null,
            branch_name: payload.branch?.name ?? null,
            cashier_id: payload.cashier?.id ?? null,
            cashier_name: payload.cashier?.name ?? null,
            customer_id: payload.customer?.id ?? null,
            customer_name: payload.customer?.name ?? null,
            customer_phone: payload.customer?.phone ?? null,
            customer_wallet_balance: payload.customer?.wallet_balance ?? 0,
            customer_wallet_status: payload.customer?.wallet_status ?? 'active',
            customer_type: payload.customer?.type ?? null,

            gross_amount: payload.summary?.gross_amount ?? 0,
            discount: payload.summary?.discount ?? 0,
            tax: payload.summary?.tax ?? 0,
            fed_tax: payload.summary?.fed_tax ?? 0,
            extra_tax: payload.summary?.extra_tax ?? 0,
            final_total: payload.summary?.final_total ?? 0,
            paid_amount: payload.summary?.paid_amount ?? 0,
            balance_due: payload.summary?.balance_due ?? 0,

            updated_at: new Date().toISOString(),
        },
        items: (payload.items ?? []).map((item) => ({
            // Keep the server's canonical refund allocation snapshot intact.
            // Refund values include sale-level discounts and header-tax penny
            // allocation, so reconstructing them from unit_price offline can
            // produce a materially different quote.
            ...item,
            sales_order: salesOrder,
            remaining_qty: item.remaining_qty ?? item.quantity,
            refunded_qty: item.refunded_qty ?? 0,
            refunded_amount: item.refunded_amount ?? 0,
        })),
        payments: (payload.payments ?? []).map((payment) => ({
            ...payment,
            sales_order: salesOrder,
        })),
    };
};

const writeRefundSaleCache = async (payloads, { replaceAll = false, expectedEpoch = captureOfflineCacheEpoch() } = {}) => {
    const records = payloads.map(normalizeRefundSaleCacheRecords).filter(Boolean);

    assertOfflineCacheEpoch(expectedEpoch);
    await offlineDB.transaction(
        'rw',
        offlineDB.sale_details,
        offlineDB.sale_items,
        offlineDB.sale_payments,
        ...(offlineDB.sales ? [offlineDB.sales] : []),
        async () => {
            // Re-check after Dexie acquires the transaction. A cache-clear may
            // have invalidated this request while it was waiting for a writer.
            assertOfflineCacheEpoch(expectedEpoch);
            if (replaceAll) {
                await offlineDB.sale_details.clear();
                await offlineDB.sale_items.clear();
                await offlineDB.sale_payments.clear();
            } else {
                for (const record of records) {
                    await offlineDB.sale_items.where('sales_order').equals(record.salesOrder).delete();
                    await offlineDB.sale_payments.where('sales_order').equals(record.salesOrder).delete();
                }
            }

            if (records.length === 0) return;

            await offlineDB.sale_details.bulkPut(records.map(({ detail }) => detail));

            const items = records.flatMap((record) => record.items);
            if (items.length) await offlineDB.sale_items.bulkPut(items);

            const payments = records.flatMap((record) => record.payments);
            if (payments.length) await offlineDB.sale_payments.bulkPut(payments);

            // Keep lean sales list rows in parity with refund cache writes.
            if (offlineDB.sales) {
                const leanRows = records.map(({ detail }) => leanSalesRowFromRefundDetail(detail)).filter(Boolean);
                if (leanRows.length) await offlineDB.sales.bulkPut(leanRows);
            }
        },
    );
};

const leanSalesRowFromRefundDetail = (detail) => {
    if (!detail?.id) return null;
    return {
        id: detail.id,
        sales_order: detail.sales_order ?? null,
        status: detail.status ?? null,
        payment_status: detail.payment_status ?? null,
        date: detail.date ?? null,
        branch_id: detail.branch_id ?? null,
        payment_branch_id: detail.payment_branch_id ?? null,
        customer_id: detail.customer_id ?? null,
        customer_name: detail.customer_name ?? null,
        customer_phone: detail.customer_phone ?? null,
        final_total: detail.final_total ?? null,
        paid_amount: detail.paid_amount ?? null,
        refunded_amount: detail.refunded_amount ?? 0,
        created_at: detail.created_at ?? null,
        updated_at: detail.updated_at ?? null,
        _cached_at: new Date().toISOString(),
    };
};

export const saveSaleFromApi = async (payload, expectedEpoch = captureOfflineCacheEpoch()) => {
    if (!payload?.sale?.sales_order) return;
    await writeRefundSaleCache([payload], { expectedEpoch });
};

export const searchSaleOffline = async (saleNumber, forceRefresh = false) => {
    if (!saleNumber) {
        return { status: 'error', message: 'Sale order number required.' };
    }

    if (forceRefresh && isConfirmedOnline()) {
        const refreshed = await cacheSaleToIDB(saleNumber);
        if (refreshed?.status === 'success') {
            const data = await getSaleDetail(saleNumber);
            if (data) return { status: 'success', data, source: 'network' };
        }
        if (refreshed?.status === 'error') {
            // Fall through to cache on failure
        }
    }

    const cachedSale = await getSaleDetail(saleNumber);
    if (cachedSale) return { status: 'success', data: cachedSale, source: forceRefresh ? 'cache_fallback' : 'offline' };
    return {
        status: 'error',
        message: 'Sale is not available offline. Open it online, search while connected, or preload refund sales first.',
    };
};

const REFUND_PRELOAD_PAGE_SIZE = 100;
const MAX_REFUND_PRELOAD_PAGES = 1000;
/**
 * How many sales the offline refund cache holds.
 *
 * There was no row bound at all — the loop ran to the server's `last_page`, so
 * opening the refund modal downloaded *every* refundable sale in the business:
 * about eight hundred sequential requests at eighty thousand sales, each page
 * carrying six eager relations and a per-page refund aggregate. It did that on
 * every open.
 *
 * It never needed to. Searching a sale **online** does not read this cache at
 * all — `searchSaleOffline(number, forceRefresh)` fetches that one sale from the
 * server (see `cacheSaleToIDB`). The preload exists solely so that a terminal
 * that later loses its connection can still refund something, and what someone
 * refunds offline is a recent sale. So it keeps the newest N, which is the same
 * shape every other document cache here already has (`MAX_RECENT_ROWS`).
 *
 * The page-count limit below stays: it guards a server reporting a nonsense
 * `last_page`, which is a different failure from this one.
 */
const REFUND_PRELOAD_MAX_ROWS = 200;

export const preloadRefundSalesToIDB = async () => {
    if (!isConfirmedOnline()) return { status: 'error', message: 'No connection available to sync sales.' };
    const cacheEpoch = captureOfflineCacheEpoch();

    const salesByOrder = new Map();
    let page = 1;
    let lastPage = 1;

    do {
        const res = await apiClient.post(
            '/refund/preloadSales',
            {
                page,
                per_page: REFUND_PRELOAD_PAGE_SIZE,
            },
            { offlinePolicy: 'read' },
        );
        assertOfflineCacheEpoch(cacheEpoch);
        const sales = res?.data?.data;
        if (!Array.isArray(sales)) {
            throw new Error(`Invalid refund sale preload response on page ${page}.`);
        }

        const reportedLastPage = Math.max(1, Number(res?.data?.last_page) || 1);
        lastPage = Math.max(lastPage, reportedLastPage);
        if (lastPage > MAX_REFUND_PRELOAD_PAGES) {
            throw new Error(`Refund sale preload exceeded the ${MAX_REFUND_PRELOAD_PAGES}-page safety limit.`);
        }

        for (const sale of sales) {
            const salesOrder = sale?.sale?.sales_order;
            if (salesOrder) salesByOrder.set(salesOrder, sale);
        }
        page += 1;
        // The endpoint orders by id descending, so what we already hold is the
        // newest slice — stopping here keeps the most refundable sales, not an
        // arbitrary prefix.
        if (salesByOrder.size >= REFUND_PRELOAD_MAX_ROWS) break;
    } while (page <= lastPage);

    // Do not expose a partially refreshed refund cache. Network reads complete
    // first; then one IndexedDB transaction replaces the authoritative set.
    const sales = [...salesByOrder.values()];
    await writeRefundSaleCache(sales, {
        replaceAll: true,
        expectedEpoch: cacheEpoch,
    });

    return {
        status: 'success',
        count: sales.length,
        pages: lastPage,
    };
};

export const getSaleDetail = async (saleNumber) => {
    if (!saleNumber) return null;

    let sale = await offlineDB.sale_details.get(saleNumber);
    if (!sale && !Number.isNaN(Number(saleNumber))) {
        sale = await offlineDB.sale_details.where('id').equals(Number(saleNumber)).first();
    }
    if (sale) {
        return normalizeSalePayload(sale);
    }

    // Bridge: sale view detail cache may already hold items/payments.
    return getSaleDetailFromSalesDetails(saleNumber);
};

const getSaleDetailFromSalesDetails = async (saleNumber) => {
    if (!offlineDB.sales_details || saleNumber == null) return null;

    let view = null;
    if (!Number.isNaN(Number(saleNumber))) {
        view = await offlineDB.sales_details.get(Number(saleNumber));
    }
    if (!view && offlineDB.sales) {
        const lean =
            (await offlineDB.sales.where('sales_order').equals(String(saleNumber)).first()) ||
            (!Number.isNaN(Number(saleNumber)) ? await offlineDB.sales.get(Number(saleNumber)) : null);
        if (lean?.id != null) {
            view = await offlineDB.sales_details.get(Number(lean.id));
        }
    }
    if (!view) {
        // Last resort: scan by sales_order on embedded sale (small cache).
        const all = await offlineDB.sales_details.toArray();
        view =
            all.find(
                (row) =>
                    String(row?.sale?.sales_order ?? row?.sales_order ?? '') === String(saleNumber) || String(row?.id ?? '') === String(saleNumber),
            ) ?? null;
    }
    if (!view?.sale || !Array.isArray(view.items) || view.items.length === 0) {
        return null;
    }

    return {
        sale: {
            id: view.sale.id,
            sales_order: view.sale.sales_order,
            date: view.sale.date,
            created_at: view.sale.created_at ?? view.sale.date,
            status: view.sale.status,
            payment_status: view.sale.payment_status,
            branch_id: view.sale.branch_id,
            payment_branch_id: view.sale.payment_branch_id ?? null,
            user_id: view.sale.user_id ?? view.cashier?.id ?? null,
            fully_refunded: view.sale.fully_refunded ?? false,
            refunded_amount: view.sale.refunded_amount ?? view.summary?.refunded_amount ?? 0,
            refunded_qty: view.sale.refunded_qty ?? 0,
        },
        branch: view.branch ?? { id: view.sale.branch_id, name: null },
        customer: view.customer ?? {
            id: view.sale.customer_id ?? null,
            name: null,
            phone: null,
            wallet_balance: 0,
            wallet_status: 'active',
            type: null,
        },
        cashier: view.cashier ?? { id: null, name: null },
        items: view.items,
        summary: view.summary ?? {},
        payments: Array.isArray(view.payments) ? view.payments : [],
    };
};

/**
 * Locate a cached refund snapshot from whatever a realtime `sales` event named.
 *
 * `sale_details` is keyed by `sales_order`, but a scoped invalidate reaches a
 * non-actor carrying only the numeric id, so both are probed.
 */
const findCachedRefundSale = async ({ id = null, salesOrder = null } = {}) => {
    if (!offlineDB.sale_details) return null;

    try {
        if (salesOrder != null && salesOrder !== '') {
            const bySalesOrder = await offlineDB.sale_details.get(String(salesOrder));
            if (bySalesOrder) return bySalesOrder;
        }

        const numericId = Number(id);
        if (Number.isFinite(numericId) && numericId > 0) {
            return (await offlineDB.sale_details.where('id').equals(numericId).first()) ?? null;
        }
    } catch (err) {
        console.warn('[offlineDB] refund snapshot probe failed', id, salesOrder, err);
    }

    return null;
};

export const hasCachedRefundSale = async (identity) => Boolean(await findCachedRefundSale(identity));

/**
 * Forget one sale's refund snapshot — header, lines and payments together.
 *
 * A half-dropped snapshot is worse than none: `normalizeSalePayload` joins
 * `sale_items` by `sales_order`, so orphaned lines would quote a refund against
 * a header that no longer exists.
 */
export const dropCachedRefundSale = async (identity) => {
    const cached = await findCachedRefundSale(identity);
    if (!cached?.sales_order) return { ok: true, reason: 'not_cached' };

    const salesOrder = cached.sales_order;
    await offlineDB.transaction('rw', offlineDB.sale_details, offlineDB.sale_items, offlineDB.sale_payments, async () => {
        await offlineDB.sale_details.delete(salesOrder);
        await offlineDB.sale_items.where('sales_order').equals(salesOrder).delete();
        await offlineDB.sale_payments.where('sales_order').equals(salesOrder).delete();
    });

    return { ok: true, reason: 'dropped', sales_order: salesOrder };
};

/**
 * Re-read a cached refund snapshot from the server after the sale moved.
 *
 * Only snapshots this session already holds are refreshed — the same rule
 * `hasCachedBranchStock` applies to POS stock. A sale nobody cached is not
 * pulled just because someone else refunded it.
 *
 * A failed refresh deliberately leaves the existing snapshot in place: it is
 * the only copy that survives the next outage, the refund modal force-refreshes
 * it when opened online, and the server re-validates remaining quantities
 * before accepting any refund.
 */
export const refreshCachedRefundSale = async (identity) => {
    const cached = await findCachedRefundSale(identity);
    if (!cached?.sales_order) return { ok: true, reason: 'not_cached' };
    if (!isConfirmedOnline()) return { ok: false, reason: 'offline', sales_order: cached.sales_order };

    const result = await cacheSaleToIDB(cached.sales_order);
    return result?.status === 'success'
        ? { ok: true, reason: 'refreshed', sales_order: cached.sales_order }
        : { ok: false, reason: 'refresh_failed', sales_order: cached.sales_order };
};

export const saveRefundToIDB = async (refundPayload) => {
    if (!refundPayload || !refundPayload.id) return;

    await offlineDB.refunds.put({
        id: refundPayload.id,
        refund_number: refundPayload.refund_number ?? null,
        sale_id: refundPayload.sale_id ?? null,
        amount: refundPayload.amount ?? 0,
        reason: refundPayload.reason ?? null,
        refund_method: refundPayload.refund_method ?? null,
        status: refundPayload.status ?? null,
        user_id: refundPayload.user_id ?? null,
        processed_by: refundPayload.processed_by ?? null,
        created_at: refundPayload.created_at ?? new Date().toISOString(),
        items: refundPayload.items ?? null,
        updated_at: new Date().toISOString(),
    });
};

export const setOfflineToken = async (token, expiresAt, userId = null) => {
    await offlineDB.auth.put({ id: 1, token, expires_at: expiresAt, user_id: userId });
};

export const getOfflineSession = async () => {
    const rec = await offlineDB.auth.get(1);
    return rec && new Date(rec.expires_at).getTime() > Date.now() ? rec : null;
};

export const getOfflineToken = async () => (await getOfflineSession())?.token ?? null;

export const clearOfflineToken = async () => {
    await offlineDB.auth.delete(1);
};

export const saveStoreState = async (data, expectedEpoch = captureOfflineCacheEpoch({ requireOwner: true })) =>
    writeAuthoritativeCache(
        'sale_drafts',
        {
            tabId: 'global',
            data,
        },
        {
            mode: 'put',
            snapshot: expectedEpoch,
        },
    );

export const loadStoreState = async () => {
    const res = await offlineDB.sale_drafts.get('global');
    return res?.data || null;
};

export const clearStoreState = async () => {
    await offlineDB.sale_drafts.delete('global');
};

async function fetchAllBranchProducts(branchId) {
    const rows = [];
    const seenVariantIds = new Set();
    const perPage = 500;

    for (let page = 1; ; page += 1) {
        const res = await apiClient.post('/branch/fetchBranchProducts', { branch_id: branchId, page, per_page: perPage }, { offlinePolicy: 'read' });
        const envelope = res?.data;
        const batch = Array.isArray(envelope) ? envelope : envelope?.data;
        if (!Array.isArray(batch)) {
            throw new Error('Invalid branch-products response.');
        }

        let newRows = 0;
        for (const row of batch) {
            const variantId = row?.product_variant_id;
            if (variantId == null) continue;
            const key = String(variantId);
            if (seenVariantIds.has(key)) continue;
            seenVariantIds.add(key);
            rows.push(row);
            newRows += 1;
        }

        // Backward compatibility for a server that still returns a bare array.
        if (Array.isArray(envelope)) {
            if (batch.length >= perPage) {
                throw new Error('Branch-products response may be truncated; cache reconciliation was skipped.');
            }
            return rows;
        }

        // `hasMore` is the direct answer — the endpoint reads one row past the
        // page rather than counting the branch, so it knows this without a
        // total. `last_page` stays as the fallback for an older server.
        if (typeof envelope?.hasMore === 'boolean') {
            if (!envelope.hasMore) return rows;
        } else {
            const lastPage = Number(envelope?.last_page);
            const currentPage = Number(envelope?.current_page ?? page);
            if (!Number.isFinite(lastPage) || currentPage >= lastPage) return rows;
        }
        if (!batch.length || newRows === 0) {
            throw new Error(`Branch-products pagination made no progress on page ${page}.`);
        }
    }
}

/** syncBranchStock owns this key format; realtime + resume paths reuse it. */
const branchStockMetaKey = (branchId) => `branch_products:${String(branchId)}`;

/**
 * Does this session hold cached POS stock for the branch?
 *
 * Realtime invalidations are addressed by branch, but a terminal only ever
 * caches the branches it sells from. Refreshing an uncached branch would pull a
 * whole catalogue nobody asked for, so membership gates the refresh.
 */
export const hasCachedBranchStock = async (branchId) => {
    if (!offlineDB.branch_products || branchId == null) return false;
    const id = Number(branchId);
    if (!Number.isFinite(id) || id <= 0) return false;

    try {
        // Rows written before v22 could persist branch_id as a number, and Dexie
        // is type-strict — probe both representations.
        const row =
            (await offlineDB.branch_products.where('branch_id').equals(String(id)).first()) ||
            (await offlineDB.branch_products.where('branch_id').equals(id).first());
        return Boolean(row);
    } catch (err) {
        console.warn('[offlineDB] cached branch stock probe failed', branchId, err);
        return false;
    }
};

/**
 * Drop every branch_products freshness marker and report the branches that had
 * one.
 *
 * Reverb does not replay: every `branch_stocks` invalidation published while the
 * socket was down is gone for good. After a resume the cached quantities are of
 * unknown age, and a surviving TTL marker would let the next syncBranchStock
 * call skip the refetch and keep serving them. Deleting markers only forces the
 * next read to the network — no cached row, and therefore no cart reservation,
 * is discarded.
 */
export const invalidateBranchStockCaches = async () => {
    if (!offlineDB.offline_sync_meta) return [];

    try {
        const rows = await offlineDB.offline_sync_meta.where('table').equals('branch_products').toArray();
        if (!rows.length) return [];

        await offlineDB.offline_sync_meta.bulkDelete(rows.map((row) => row.key));

        return rows.map((row) => Number(row.scope)).filter((id) => Number.isFinite(id) && id > 0);
    } catch (err) {
        console.warn('[offlineDB] could not invalidate branch stock TTLs', err);
        return [];
    }
};

/**
 * Concurrent POS tabs, the realtime refresh and the reconnect resync can all ask
 * for the same branch at once. Registered only past the TTL check, so a forced
 * caller never inherits another caller's `ttl_skip`.
 */
const branchStockInFlight = new Map();

/**
 * Sync branch stock into Dexie while preserving in-cart reservations.
 * @param {string|number} branchId
 * @param {boolean} [force=false] — bypass short TTL and always refetch
 */
export const syncBranchStock = async (branchId, force = false) => {
    if (!branchId || !isConfirmedOnline()) return { ok: false, reason: 'offline_or_no_branch' };
    const cacheEpoch = captureOfflineCacheEpoch();

    const TTL_MS = 30_000;
    const branchKey = String(branchId);
    const metaKey = branchStockMetaKey(branchKey);

    if (!force && offlineDB.offline_sync_meta) {
        try {
            const meta = await offlineDB.offline_sync_meta.get(metaKey);
            if (meta?.last_success_at && Date.now() - new Date(meta.last_success_at).getTime() < TTL_MS) {
                return { ok: true, reason: 'ttl_skip' };
            }
        } catch {
            /* ignore meta read errors */
        }
    }

    const pending = branchStockInFlight.get(branchKey);
    if (pending) return pending;

    const run = syncBranchStockNow(branchId, branchKey, metaKey, cacheEpoch);
    branchStockInFlight.set(branchKey, run);
    try {
        return await run;
    } finally {
        if (branchStockInFlight.get(branchKey) === run) branchStockInFlight.delete(branchKey);
    }
};

/**
 * One `branch_products` row from one server row, or null if the id is unusable.
 *
 * Shared by the full reconcile and the targeted variant refresh so the two
 * cannot drift: a partial refresh that wrote a subtly different shape would put
 * rows in the cache the POS reads differently depending on which path last
 * touched them. `reservedByKey` carries the in-cart quantities forward — the
 * server has no opinion about those and must never be allowed to erase them.
 */
function branchProductRecord(v, branchKey, reservedByKey) {
    const variantId = Number(v?.product_variant_id);
    if (!Number.isSafeInteger(variantId) || variantId <= 0) return null;

    const key = `${branchKey}-${variantId}`;
    const salesPrice = Number(v.sales_price ?? 0);
    const taxPct = Number(v.tax_percentage ?? 0);
    const reserved = reservedByKey.get(key) ?? 0;

    return {
        key,
        branch_id: branchKey,
        variant_id: variantId,
        real_qty: Math.max(Number(v.real_qty ?? 0) || 0, 0),
        reserved_qty: Math.max(Number(reserved) || 0, 0),
        branch_name: v.branch_name ?? '',
        product_name: v.product_name ?? '',
        variant_name: v.variant_name ?? '',
        sku: v.sku ?? '',
        code: v.code ?? '',
        display_name: `${v.product_name ?? ''} - ${v.variant_name ?? ''}`,
        sales_price: Number.isFinite(salesPrice) ? salesPrice : 0,
        tax_percentage: Number.isFinite(taxPct) ? taxPct : 0,
        third_schedule: v.third_schedule === true || v.third_schedule === 1 || v.third_schedule === '1',
        // Printed retail price, and the basis the flag above puts the tax on.
        // Null when no MRP was ever recorded, which is what the calculator
        // reads as "fall back to the line's own value".
        mrp: Number(v.mrp ?? 0) > 0 ? Number(v.mrp) : null,
        unit_abbr: v.unit_abbr ?? '',
        // Not indexed, so no schema version is involved — Dexie only versions key
        // paths. Discrete unless the server says otherwise, matching
        // units.allows_fractional's own default.
        allows_fractional: v.allows_fractional === true || v.allows_fractional === 1 || v.allows_fractional === '1',
        discount_type: v.discount_type === 'percent' ? 'percent' : v.discount_type === 'rs' ? 'rs' : null,
        discount_value: Number.isFinite(Number(v.discount_value)) ? Number(v.discount_value) : 0,
        // Withdrawn from sale at this branch — already the OR of the company
        // switch and the branch's own, resolved server-side. Not indexed, so no
        // schema version is involved; Dexie only versions key paths. Saleable
        // unless the server says otherwise, so a row cached before the column
        // existed keeps behaving as it did rather than vanishing from the till.
        not_for_sale: v.not_for_sale === true || v.not_for_sale === 1 || v.not_for_sale === '1',
        orphaned: false,
        updated_at: new Date().toISOString(),
    };
}

/** A targeted refresh may name at most this many variants before a full read is cheaper. */
export const MAX_TARGETED_STOCK_VARIANTS = 200;

/**
 * Refresh only the variants a `branch_stocks` event named.
 *
 * A sale moves three quantities. The old answer to that was to bust the branch's
 * freshness marker and re-paginate its entire catalogue — about sixty requests
 * for a branch holding thirty thousand in-stock variants, on every terminal that
 * had the branch cached, and twice per sale because `StockService::apply` and
 * `ProcessDomainOutbox` each emit the invalidate. This asks for the named rows
 * instead: one request, whatever the catalogue size.
 *
 * A named variant the server does not return has dropped to zero stock or left
 * the branch — the endpoint filters `real_qty > 0` — so it is removed here, with
 * the same courtesy the full reconcile shows: a row still holding a cart
 * reservation is kept at zero sellable and marked orphaned rather than deleted
 * out from under the operator.
 *
 * The freshness marker is deliberately left alone. It records when the whole
 * branch was last reconciled, and patching three rows does not make that older
 * or newer — busting it here would send the very next POS read back to the full
 * pagination this exists to avoid.
 */
export const syncBranchStockVariants = async (branchId, variantIds) => {
    if (!branchId || !isConfirmedOnline()) return { ok: false, reason: 'offline_or_no_branch' };

    const ids = [...new Set((Array.isArray(variantIds) ? variantIds : []).map(Number))].filter((id) => Number.isSafeInteger(id) && id > 0);
    if (ids.length === 0 || ids.length > MAX_TARGETED_STOCK_VARIANTS) {
        return { ok: false, reason: 'not_targetable' };
    }

    const branchKey = String(branchId);
    const cacheEpoch = captureOfflineCacheEpoch();

    try {
        const res = await apiClient.post(
            '/branch/fetchBranchProducts',
            { branch_id: branchId, product_variant_ids: ids, limit: MAX_TARGETED_STOCK_VARIANTS },
            { offlinePolicy: 'read', skipProgress: true },
        );
        // The bare-array response: `limit` mode, no pagination envelope.
        const rows = Array.isArray(res?.data) ? res.data : res?.data?.data;
        if (!Array.isArray(rows)) throw new Error('Invalid branch-products response.');
        assertOfflineCacheEpoch(cacheEpoch);

        return await offlineDB.transaction('rw', offlineDB.branch_products, async () => {
            assertOfflineCacheEpoch(cacheEpoch);

            const keys = ids.map((id) => `${branchKey}-${id}`);
            const existing = (await offlineDB.branch_products.bulkGet(keys)).filter(Boolean);
            const reservedByKey = new Map();
            for (const row of existing) {
                const reserved = Number(row.reserved_qty ?? 0);
                if (reserved > 0) reservedByKey.set(row.key, reserved);
            }

            const returned = new Set();
            const records = [];
            for (const row of rows) {
                const record = branchProductRecord(row, branchKey, reservedByKey);
                if (!record) continue;
                returned.add(record.key);
                records.push(record);
            }

            const orphans = [];
            const removals = [];
            for (const row of existing) {
                if (returned.has(row.key)) continue;
                if (Number(row.reserved_qty ?? 0) > 0) {
                    orphans.push({ ...row, real_qty: 0, orphaned: true, updated_at: new Date().toISOString() });
                } else {
                    removals.push(row.key);
                }
            }

            if (records.length || orphans.length) await offlineDB.branch_products.bulkPut([...records, ...orphans]);
            if (removals.length) await offlineDB.branch_products.bulkDelete(removals);

            return { ok: true, count: records.length, orphans: orphans.length, removed: removals.length };
        });
    } catch (err) {
        if (isStaleOfflineCacheWrite(err)) return { ok: false, reason: 'cache_session_changed' };
        console.warn('[syncBranchStockVariants] failed', { branchId, err });
        return { ok: false, reason: 'error', error: err };
    }
};

async function syncBranchStockNow(branchId, branchKey, metaKey, cacheEpoch) {
    try {
        // The branch-products endpoint already applies the server's canonical
        // latest-receiving price/tax rules. Keeping that decision server-side
        // makes POS and exchange validation use the same quote.
        const allProducts = await fetchAllBranchProducts(branchId);
        assertOfflineCacheEpoch(cacheEpoch);

        const result = await offlineDB.transaction('rw', offlineDB.branch_products, offlineDB.offline_sync_meta, async () => {
            assertOfflineCacheEpoch(cacheEpoch);
            // Older schema versions could persist branch_id as a number. Match
            // both representations while normalizing refreshed rows to strings.
            // Indexed, not a cursor scan: this runs inside the write
            // transaction, so a full-store walk held the lock while
            // deserializing every other branch's rows too.
            const existingRows = await offlineDB.branch_products
                .where('branch_id')
                .anyOf([branchKey, Number(branchKey)])
                .toArray();
            const reservedByKey = new Map();
            for (const row of existingRows) {
                const reserved = Number(row.reserved_qty ?? 0);
                if (reserved > 0) reservedByKey.set(row.key, reserved);
            }

            const incomingKeys = new Set();
            const allRecords = allProducts.flatMap((v) => {
                const record = branchProductRecord(v, branchKey, reservedByKey);
                if (!record) return [];
                incomingKeys.add(record.key);
                return [record];
            });

            // Stale rows still reserved: keep, zero sellable stock, mark orphaned
            const orphanRecords = [];
            for (const row of existingRows) {
                if (incomingKeys.has(row.key)) continue;
                const reserved = Number(row.reserved_qty ?? 0);
                if (reserved > 0) {
                    orphanRecords.push({
                        ...row,
                        real_qty: 0,
                        orphaned: true,
                        updated_at: new Date().toISOString(),
                    });
                } else {
                    await offlineDB.branch_products.delete(row.key);
                }
            }

            await offlineDB.branch_products.bulkPut([...allRecords, ...orphanRecords]);

            if (offlineDB.offline_sync_meta) {
                await offlineDB.offline_sync_meta.put({
                    key: metaKey,
                    table: 'branch_products',
                    scope: branchKey,
                    last_success_at: new Date().toISOString(),
                    row_count: allRecords.length,
                    partial: false,
                });
            }

            return {
                ok: true,
                count: allRecords.length,
                orphans: orphanRecords.length,
                partial: false,
            };
        });

        return result;
    } catch (err) {
        if (isStaleOfflineCacheWrite(err)) {
            return { ok: false, reason: 'cache_session_changed' };
        }
        const isQuota = err?.name === 'QuotaExceededError' || /quota/i.test(String(err?.message || ''));
        console.error('[syncBranchStock] failed', { branchId, isQuota, err });
        if (isQuota) {
            console.warn('[syncBranchStock] storage quota exceeded — cart reservations were not wiped');
        }
        throw err;
    }
}
/* ------------------------------------------------------------------ *
 * POS search catalogue
 * ------------------------------------------------------------------ */

/** Max catalogue rows pulled per index before the AND-filter narrows them. */
const CATALOG_SCAN_CAP = 200;
/** Max candidates carried into the branch_products join. */
const CATALOG_CANDIDATE_CAP = 50;

/**
 * Persist rows from a /sale/search-products response into the search catalogue.
 * Upsert only — never deletes, since any one response is a partial view.
 */
export const upsertPosCatalog = async (rows, snapshot = captureOfflineCacheEpoch()) => {
    if (!offlineDB.pos_catalog || !Array.isArray(rows) || rows.length === 0) return 0;

    const now = new Date().toISOString();
    const records = rows.flatMap((row) => {
        const id = Number(row?.id);
        if (!Number.isSafeInteger(id) || id <= 0) return [];

        const name = String(row.name ?? '');
        const sku = String(row.sku ?? '');
        const code = String(row.code ?? '');

        return [
            {
                id,
                name,
                sku,
                code,
                name_lower: name.toLowerCase(),
                sku_lower: sku.toLowerCase(),
                code_lower: code.toLowerCase(),
                name_tokens: tokenizeName(name),
                unit_abbr: row.unit_abbr ?? '',
                allows_fractional: row.allows_fractional === true || row.allows_fractional === 1 || row.allows_fractional === '1',
                tax_percentage: Number(row.tax_percentage ?? 0) || 0,
                third_schedule: row.third_schedule === true || row.third_schedule === 1 || row.third_schedule === '1',
                // The basis the flag above puts the tax on. Cached with it so a
                // searched line and a prefetched one quote the same tax.
                mrp: Number(row.mrp ?? 0) > 0 ? Number(row.mrp) : null,
                // Server-resolved against one branch, so it is not valid catalogue-wide.
                // Only ever displayed for rows with no branch_products row, which cannot be sold.
                fallback_sales_price: Number(row.sales_price ?? 0) || 0,
                discount_type: row.discount_type === 'percent' ? 'percent' : row.discount_type === 'rs' ? 'rs' : null,
                discount_value: Number(row.discount_value ?? 0) || 0,
                // Withdrawn from sale company-wide. Always false on the way in —
                // /sale/search-products filters those rows out, so one never
                // arrives here — and that is the point: a variant reappearing in
                // a search response IS the news that it is back on sale, so this
                // is what clears a `true` left behind by
                // applyPosCatalogVariantChange while the till was offline.
                not_for_sale: row.not_for_sale === true || row.not_for_sale === 1 || row.not_for_sale === '1',
                _cached_at: now,
            },
        ];
    });

    if (!records.length) return 0;
    await writeAuthoritativeCache('pos_catalog', records, { mode: 'bulkPut', snapshot });
    return records.length;
};

/** Catalogue discount shape, matching what `upsertPosCatalog` stores. */
const normalizedCatalogDiscount = (type, value) => ({
    discount_type: type === 'percent' ? 'percent' : type === 'rs' ? 'rs' : null,
    discount_value: Number(value ?? 0) || 0,
});

/**
 * Drop variants from the POS search catalogue.
 *
 * `/sale/search-products` only ever returns `status = 'active'` variants and
 * `upsertPosCatalog` never deletes, so without this a deleted or paused variant
 * stays findable in local search forever — rendered as `unavailable` because its
 * branch_products row is gone, but still occupying a result slot.
 */
export const removePosCatalogVariants = async (variantIds = []) => {
    if (!offlineDB.pos_catalog || !Array.isArray(variantIds)) return 0;

    const ids = [...new Set(variantIds.map((id) => Number(id)))].filter((id) => Number.isSafeInteger(id) && id > 0);
    if (!ids.length) return 0;

    await offlineDB.pos_catalog.bulkDelete(ids);
    return ids.length;
};

/**
 * Apply a realtime `product_variants` change to the POS search catalogue.
 *
 * The catalogue is populated lazily — a row exists only for a variant some
 * search already returned — so a variant this session never searched for is
 * left alone. Creating one here would publish a row with no `tax_percentage`,
 * `third_schedule`, `unit_abbr` or `allows_fractional`, since those live on the
 * product's category and unit and are absent from the variant payload, and the
 * POS quotes tax and steps quantity from exactly those fields.
 *
 * For the same reason the update is a patch, not a replace: identity and price
 * come from the event, while the category/unit-derived fields keep the values
 * the server resolved on the last search.
 */
export const applyPosCatalogVariantChange = async (row) => {
    if (!offlineDB.pos_catalog || !row || typeof row !== 'object') {
        return { ok: false, reason: 'bad_payload' };
    }

    const id = Number(row.id);
    if (!Number.isSafeInteger(id) || id <= 0) return { ok: false, reason: 'bad_payload' };

    const existing = await offlineDB.pos_catalog.get(id);
    if (!existing) return { ok: true, reason: 'not_cached' };

    // Anything the POS may no longer sell leaves the catalogue outright.
    if (row.status != null && String(row.status) !== 'active') {
        await offlineDB.pos_catalog.delete(id);
        return { ok: true, reason: 'removed' };
    }

    const name = row.name != null ? String(row.name) : String(existing.name ?? '');
    const sku = row.sku != null ? String(row.sku) : String(existing.sku ?? '');
    const code = row.code != null ? String(row.code) : String(existing.code ?? '');

    await offlineDB.pos_catalog.put({
        ...existing,
        id,
        name,
        sku,
        code,
        name_lower: name.toLowerCase(),
        sku_lower: sku.toLowerCase(),
        code_lower: code.toLowerCase(),
        name_tokens: tokenizeName(name),
        fallback_sales_price: Number(row.sales_price ?? existing.fallback_sales_price ?? 0) || 0,
        // `?? existing` rather than a bare read: a payload that omits the
        // discount keys must not be read as "discount removed".
        ...normalizedCatalogDiscount(row.discount_type ?? existing.discount_type, row.discount_value ?? existing.discount_value),
        // Patched, not deleted like the paused variant above, for the same
        // reason this whole write is a patch: the category- and unit-derived
        // fields cannot be rebuilt from a variant event, so dropping the row
        // would leave the product missing from local search until an online
        // search re-resolved them — and being withdrawn from sale is a setting
        // that gets switched back. searchPosCatalog reads the flag and hides it
        // meanwhile, which is the same outcome without the loss.
        not_for_sale:
            (row.not_for_sale ?? existing.not_for_sale) === true ||
            (row.not_for_sale ?? existing.not_for_sale) === 1 ||
            (row.not_for_sale ?? existing.not_for_sale) === '1',
        _cached_at: new Date().toISOString(),
    });

    return { ok: true, reason: 'patched' };
};

const startsWithOrEmpty = async (table, index, value, cap) => {
    if (!value) return [];
    return table.where(index).startsWith(value).limit(cap).toArray();
};

/**
 * Local POS search: word-prefix over the catalogue, quantities joined from
 * branch_products across the supplied branch pool.
 *
 * Returns the same row shape the online leg maps to, so both legs render through
 * one path.
 *
 * `originBranchId` is the till's own branch, and it is what decides whether a
 * product withdrawn from sale is hidden — mirroring SaleController::
 * searchProducts, which keys the same rule on `origin_branch_id` rather than on
 * the fulfillment pool. Omitting it falls back to the company-wide switch alone,
 * which is what a company order with no origin branch gets server-side too.
 *
 * @param {{ term: string, branchIds?: Array<number|string>, originBranchId?: number|string|null, limit?: number }} params
 */
export const searchPosCatalog = async ({ term, branchIds = [], originBranchId = null, limit = 25 }) => {
    if (!offlineDB.pos_catalog) return [];

    const normalized = normalizeTerm(term);
    const tokens = tokenizeTerm(normalized);
    if (!normalized || tokens.length === 0) return [];

    const driver = selectDriverToken(tokens);

    const [byToken, bySku, byCode] = await Promise.all([
        startsWithOrEmpty(offlineDB.pos_catalog, 'name_tokens', driver, CATALOG_SCAN_CAP),
        startsWithOrEmpty(offlineDB.pos_catalog, 'sku_lower', normalized, CATALOG_CANDIDATE_CAP),
        startsWithOrEmpty(offlineDB.pos_catalog, 'code_lower', normalized, CATALOG_CANDIDATE_CAP),
    ]);

    const candidates = new Map();
    for (const row of byToken) {
        // The index matched one token; every remaining token must match too.
        if (matchesAllTokens(row.name_tokens, tokens)) candidates.set(row.id, row);
    }
    // SKU/code match the whole term and are never tokenised — barcodes contain separators.
    for (const row of [...bySku, ...byCode]) candidates.set(row.id, row);
    if (candidates.size === 0) return [];

    const rows = [...candidates.values()].slice(0, CATALOG_CANDIDATE_CAP);
    const ids = rows.map((row) => row.id);
    const pool = [...new Set(branchIds.map((id) => Number(id)).filter((id) => id > 0))];
    const origin = Number(originBranchId) || null;

    // The origin branch is fetched even when it is not in the pool, because its
    // row is what says whether this till may sell the product at all. `pool`
    // itself is left alone — it is what the quantities are summed over, and
    // adding a branch to it would add that branch's stock to the total.
    const lookupBranchIds = origin && !pool.includes(origin) ? [...pool, origin] : pool;

    let stockByKey = new Map();
    if (lookupBranchIds.length && ids.length) {
        const pairs = lookupBranchIds.flatMap((branchId) => ids.map((variantId) => [String(branchId), variantId]));
        const stockRows = await offlineDB.branch_products.where('[branch_id+variant_id]').anyOf(pairs).toArray();
        stockByKey = new Map(stockRows.map((row) => [`${Number(row.branch_id)}-${Number(row.variant_id)}`, row]));
    }

    const results = rows.flatMap((row) => {
        // Withdrawn company-wide, or at this till's branch. The branch answer
        // lives on the branch_products row, which already carries the OR of both
        // switches; where the origin branch has no row the company flag on the
        // catalogue entry is the only answer there is, and it is the right one.
        const originStock = origin ? stockByKey.get(`${origin}-${row.id}`) : null;
        if (row.not_for_sale === true || originStock?.not_for_sale === true) return [];

        let available = 0;
        let hasRow = false;
        let priceRow = null;
        const byBranch = [];

        // Pool order mirrors StockAvailability::candidateBranches priority, so the
        // first branch holding the variant is also the server's pricing branch.
        for (const branchId of pool) {
            const stock = stockByKey.get(`${branchId}-${row.id}`);
            if (!stock) continue;
            hasRow = true;
            if (priceRow === null) priceRow = stock;

            const qty = Math.max(Number(stock.real_qty ?? 0) - Number(stock.reserved_qty ?? 0), 0);
            available += qty;
            if (qty > 0) {
                byBranch.push({
                    branch_id: branchId,
                    branch_name: stock.branch_name || '',
                    available: qty,
                });
            }
        }

        // Locally, out_of_stock and unavailable collapse: fetchBranchProducts only
        // caches rows with real_qty > 0, so a zero-stock item has no row. The remote
        // leg corrects the label on the first uncovered search.
        const status = available > 0 ? 'in_stock' : hasRow ? 'out_of_stock' : 'unavailable';

        const salesPrice = priceRow ? Number(priceRow.sales_price ?? 0) || 0 : Number(row.fallback_sales_price ?? 0) || 0;
        const taxPct = priceRow ? Number(priceRow.tax_percentage ?? 0) || 0 : Number(row.tax_percentage ?? 0) || 0;
        const discountType = priceRow ? priceRow.discount_type : row.discount_type;

        return [
            {
                id: row.id,
                name: row.name,
                sku: row.sku || '',
                code: row.code || '',
                rate: salesPrice,
                maxQty: available,
                stock_status: status,
                branchCount: byBranch.length,
                by_branch: byBranch,
                tax_rate: taxPct > 0 ? (salesPrice * taxPct) / (100 + taxPct) : 0,
                tax_percentage: taxPct,
                third_schedule: Boolean(priceRow ? priceRow.third_schedule : row.third_schedule),
                // Whichever cache actually carries it, rather than the branch-row-
                // then-catalogue order of the fields either side. Those two are
                // per-branch and a blank is a real answer; MRP is a property of the
                // goods, so a branch row written before the column existed must fall
                // through to the catalogue row instead of blanking the basis — which
                // would put a third-schedule line's tax back on the discounted value.
                mrp: Number(priceRow?.mrp ?? row?.mrp ?? 0) > 0 ? Number(priceRow?.mrp ?? row?.mrp) : null,
                unit_abbr: (priceRow ? priceRow.unit_abbr : row.unit_abbr) || '',
                // Drives the cart's quantity step. Read from the branch row when
                // there is one, so a catalogue entry cached before the field
                // existed still resolves correctly for a branch that has it.
                allows_fractional: Boolean(priceRow ? priceRow.allows_fractional : row.allows_fractional),
                discount: Number((priceRow ? priceRow.discount_value : row.discount_value) ?? 0) || 0,
                discount_type: discountType === 'percent' ? 'percent' : 'rs',
            },
        ];
    });

    const statusRank = { in_stock: 0, out_of_stock: 1, unavailable: 2 };
    results.sort((a, b) => {
        const rank = (statusRank[a.stock_status] ?? 2) - (statusRank[b.stock_status] ?? 2);
        if (rank !== 0) return rank;
        return String(a.name).toLowerCase().localeCompare(String(b.name).toLowerCase());
    });

    return results.slice(0, limit);
};

/**
 * Release every in-cart reservation, for one branch or the whole cache.
 *
 * Runs on POS draft restore, so it is on the boot path of the busiest screen.
 * It used to rewrite *every* cached row with one awaited `put()` each — five
 * thousand serialized IndexedDB round-trips at load scale for a till whose cart
 * had held three lines, which is most of a second of boot spent writing zero
 * over zero. Only rows that actually hold a reservation are rewritten now, and
 * they go in one `bulkPut`.
 */
export const resetAllReservations = async (branchId) => {
    const records = branchId
        ? await offlineDB.branch_products
              .where('branch_id')
              .anyOf([String(branchId), Number(branchId)])
              .toArray()
        : await offlineDB.branch_products.toArray();

    const held = records.filter((record) => Number(record.reserved_qty ?? 0) > 0);
    if (!held.length) return;

    await offlineDB.branch_products.bulkPut(held.map((record) => ({ ...record, reserved_qty: 0 })));
};

export const getAvailableStock = async (variantId, branchId) => {
    const key = `${branchId}-${variantId}`;
    const item = await offlineDB.branch_products.get(key);
    const real = Number(item?.real_qty ?? 0);
    const reserved = Number(item?.reserved_qty ?? 0);
    return Math.max(real - reserved, 0);
};

export const reserveStock = async (variantId, branchId, qty) => {
    const amount = Number(qty);
    if (!Number.isFinite(amount) || amount <= 0) return false;
    const key = `${branchId}-${variantId}`;
    return offlineDB.transaction('rw', offlineDB.branch_products, async () => {
        const item = await offlineDB.branch_products.get(key);
        if (!item) return false;
        const available = Math.max(Number(item.real_qty ?? 0) - Number(item.reserved_qty ?? 0), 0);
        if (amount > available) return false;
        item.reserved_qty = Number(item.reserved_qty ?? 0) + amount;
        await offlineDB.branch_products.put(item);
        return true;
    });
};

export const releaseStock = async (variantId, branchId, qty) => {
    const amount = Number(qty);
    if (!Number.isFinite(amount) || amount <= 0) return false;
    const key = `${branchId}-${variantId}`;
    return offlineDB.transaction('rw', offlineDB.branch_products, async () => {
        const item = await offlineDB.branch_products.get(key);
        if (!item) return false;
        item.reserved_qty = Math.max(Number(item.reserved_qty ?? 0) - amount, 0);
        await offlineDB.branch_products.put(item);
        return true;
    });
};

export const finalizeSale = async (items, branchId) => {
    await offlineDB.transaction('rw', offlineDB.branch_products, async () => {
        for (const item of items) {
            const key = `${branchId}-${item.id}`;
            const stock = await offlineDB.branch_products.get(key);
            if (!stock) continue;
            stock.real_qty = Number(stock.real_qty) - Number(item.qty);
            stock.reserved_qty = Math.max(Number(stock.reserved_qty ?? 0) - Number(item.qty), 0);
            await offlineDB.branch_products.put(stock);
        }
    });
};

/**
 * Persist server sale_item_allocations and decrement local branch_products per
 * fulfillment branch (company / multi-branch success path).
 */
export const saveSaleAllocations = async (saleId, allocations = []) => {
    if (!offlineDB.sale_allocations || !Array.isArray(allocations) || allocations.length === 0) {
        return;
    }
    const rows = allocations
        .map((row) => ({
            id: Number(row.id),
            sale_id: Number(row.sale_id ?? saleId),
            sale_item_id: Number(row.sale_item_id ?? 0) || null,
            product_variant_id: Number(row.product_variant_id),
            branch_id: Number(row.branch_id),
            quantity: Math.max(0, Math.floor(Number(row.quantity) || 0)),
        }))
        .filter(
            (row) =>
                Number.isSafeInteger(row.id) &&
                row.id > 0 &&
                Number.isSafeInteger(row.product_variant_id) &&
                row.product_variant_id > 0 &&
                Number.isSafeInteger(row.branch_id) &&
                row.branch_id > 0 &&
                row.quantity > 0,
        );
    if (!rows.length) return;
    await offlineDB.sale_allocations.bulkPut(rows);
};

export const getSaleAllocations = async (saleId) => {
    if (!offlineDB.sale_allocations || saleId == null) return [];
    return offlineDB.sale_allocations.where('sale_id').equals(Number(saleId)).toArray();
};

/**
 * Apply server allocation rows to local stock caches after a successful sale.
 * Also clears origin-branch reservations when originBranchId is provided.
 */
export const finalizeSaleAllocations = async (allocations = [], { originBranchId = null, reservedItems = [] } = {}) => {
    if (!Array.isArray(allocations) || allocations.length === 0) return;

    await offlineDB.transaction('rw', offlineDB.branch_products, offlineDB.sale_allocations, async () => {
        for (const row of allocations) {
            const branchId = Number(row.branch_id);
            const variantId = Number(row.product_variant_id);
            const qty = Math.max(0, Math.floor(Number(row.quantity) || 0));
            if (!branchId || !variantId || qty <= 0) continue;
            const key = `${branchId}-${variantId}`;
            const stock = await offlineDB.branch_products.get(key);
            if (!stock) continue;
            stock.real_qty = Math.max(0, Number(stock.real_qty ?? 0) - qty);
            await offlineDB.branch_products.put(stock);
        }

        if (originBranchId && Array.isArray(reservedItems)) {
            for (const item of reservedItems) {
                const key = `${originBranchId}-${Number(item.id)}`;
                const stock = await offlineDB.branch_products.get(key);
                if (!stock) continue;
                stock.reserved_qty = Math.max(0, Number(stock.reserved_qty ?? 0) - Math.max(0, Math.floor(Number(item._lastQty ?? item.qty) || 0)));
                await offlineDB.branch_products.put(stock);
            }
        }
    });

    await saveSaleAllocations(allocations[0]?.sale_id ?? null, allocations);
};

/** Display-only company availability cache — never authorize a sale from this. */
export const cacheVariantAvailability = async (rows = []) => {
    if (!offlineDB.variant_availability || !Array.isArray(rows)) return;
    const mapped = rows
        .map((row) => {
            const variantId = Number(row.product_variant_id ?? row.variant_id);
            if (!Number.isSafeInteger(variantId) || variantId <= 0) return null;
            return {
                key: String(variantId),
                variant_id: variantId,
                aggregate_available: Math.max(0, Math.floor(Number(row.aggregate_available) || 0)),
                by_branch: Array.isArray(row.by_branch) ? row.by_branch : [],
                as_of: row.as_of ?? new Date().toISOString(),
            };
        })
        .filter(Boolean);
    if (mapped.length) await offlineDB.variant_availability.bulkPut(mapped);
};

export const getVariantAvailability = async (variantId) => {
    if (!offlineDB.variant_availability || variantId == null) return null;
    return offlineDB.variant_availability.get(String(variantId));
};

export const canAddToCart = async (variantId, branchId, qty) => {
    const key = `${branchId}-${variantId}`;
    const stock = await offlineDB.branch_products.get(key);
    const real = Number(stock?.real_qty ?? 0);
    const reserved = Number(stock?.reserved_qty ?? 0);
    const available = real - reserved;
    const requested = Number(qty);
    return {
        ok: Number.isFinite(requested) && requested > 0 && requested <= available,
        available: Math.max(available, 0),
    };
};

// ------------------------------------------------------------------
// Domain detail cache helpers (branches / purchases / suppliers / customers)
// ------------------------------------------------------------------

const detailResult = (data, source, cachedAt = null, unavailableSections = []) => ({
    data,
    source,
    cachedAt: cachedAt ?? data?._cached_at ?? null,
    isStale: source === 'cache',
    unavailableSections,
});

const isAuthoritativeDetailDenial = (err) => [401, 403, 404].includes(Number(err?.response?.status));

async function saveDomainDetail(table, id, payload, expectedEpoch = captureOfflineCacheEpoch()) {
    if (!offlineDB[table] || id == null || !payload) return;
    const row = {
        ...payload,
        id: Number(id) || id,
        _cached_at: new Date().toISOString(),
    };
    await writeAuthoritativeCache(table, row, {
        mode: 'put',
        snapshot: expectedEpoch,
    });
    return row;
}

async function getDomainDetail(table, id) {
    if (!offlineDB[table] || id == null) return null;
    const row = (await offlineDB[table].get(Number(id) || id)) || (await offlineDB[table].get(id)) || null;
    if (row) touchDetailAccess(table, row.id ?? id);
    return row;
}

/**
 * Record that a cached detail was actually read.
 *
 * Eviction used to order by `_cached_at`, which is a *write* time and never
 * changes — so the record an operator opens every morning was discarded before
 * one fetched once months ago and never looked at again. That is FIFO wearing an
 * LRU label. `_accessed_at` is the real signal.
 *
 * Fire-and-forget on purpose: a read must not wait on a bookkeeping write, and a
 * lost stamp costs nothing worse than one row being evicted a little early.
 */
export function touchDetailAccess(table, id) {
    if (!offlineDB[table] || id == null) return;

    offlineDB[table].update(id, { _accessed_at: new Date().toISOString() }).catch(() => {
        /* the row may have just been evicted; nothing to record */
    });
}

/**
 * Pin a cached detail so storage pressure cannot take it.
 *
 * This is what "Make available offline" has to mean: the operator asked for a
 * guarantee, and silently evicting it under quota pressure breaks exactly the
 * promise the button made.
 */
export async function setDetailPinned(table, id, pinned = true) {
    if (!offlineDB[table] || id == null) return false;

    const updated = await offlineDB[table].update(id, {
        _pinned: pinned ? 1 : 0,
        _accessed_at: new Date().toISOString(),
    });

    return updated > 0;
}

export const saveBranchDetail = (payload, expectedEpoch) => saveDomainDetail('branch_details', payload?.id, payload, expectedEpoch);
export const getBranchDetail = (id) => getDomainDetail('branch_details', id);
export const deleteBranchDetail = async (id) => {
    if (offlineDB.branch_details) await offlineDB.branch_details.delete(Number(id) || id);
};

export const fetchBranchDetail = async (id, { forceRefresh = false } = {}) => {
    if (!id) return detailResult(null, 'none');
    if (!isConfirmedOnline()) {
        const cached = await getBranchDetail(id);
        return cached ? detailResult(cached, 'cache') : detailResult(null, 'none');
    }
    if (!forceRefresh) {
        const cached = await getBranchDetail(id);
        // stale-while-revalidate: return cache, refresh in background below when forced only
        if (cached && !forceRefresh) {
            /* continue to network for freshness when online */
        }
    }
    const cacheEpoch = captureOfflineCacheEpoch();
    try {
        const res = await apiClient.post(`/branch/show/${id}`, {}, { offlinePolicy: 'read' });
        if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
        const data = res?.data?.branch ?? res?.data?.data ?? res?.data;
        if (data) {
            const lean = { ...data };
            delete lean.cash_on_hand;
            delete lean.cashOnHand;
            await saveBranchDetail(lean, cacheEpoch);
            if (offlineDB.branches && lean.id) {
                assertOfflineCacheEpoch(cacheEpoch);
                await offlineDB.branches.put({
                    id: lean.id,
                    name: lean.name ?? '',
                    status: lean.status ?? null,
                    city: lean.city ?? null,
                    country: lean.country ?? null,
                    holds_stock: lean.holds_stock ?? true,
                    can_originate_sales: lean.can_originate_sales ?? true,
                    can_originate_purchases: lean.can_originate_purchases ?? true,
                    can_receive_stock: lean.can_receive_stock ?? true,
                    _cached_at: new Date().toISOString(),
                    updated_at: lean.updated_at ?? null,
                });
            }
            return detailResult(data, 'network');
        }
    } catch (err) {
        if (isStaleOfflineCacheWrite(err) || !isOfflineCacheEpochCurrent(cacheEpoch)) {
            return detailResult(null, 'none');
        }
        console.warn('[fetchBranchDetail] network failed', err);
        if (isAuthoritativeDetailDenial(err)) {
            await deleteBranchDetail(id);
            return detailResult(null, 'none');
        }
    }
    if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
    const cached = await getBranchDetail(id);
    return cached ? detailResult(cached, 'cache', null, ['cash_on_hand', 'cashOnHand']) : detailResult(null, 'none');
};

export const savePurchaseDetail = (payload, expectedEpoch) => saveDomainDetail('purchase_details', payload?.id, payload, expectedEpoch);
export const getPurchaseDetail = (id) => getDomainDetail('purchase_details', id);
export const deletePurchaseDetail = async (id) => {
    if (offlineDB.purchase_details) await offlineDB.purchase_details.delete(Number(id) || id);
};

export const fetchPurchaseDetail = async (id, { forceRefresh = false } = {}) => {
    if (!id) return detailResult(null, 'none');
    if (!isConfirmedOnline()) {
        const cached = await getPurchaseDetail(id);
        return cached ? detailResult(cached, 'cache') : detailResult(null, 'none');
    }
    const cacheEpoch = captureOfflineCacheEpoch();
    try {
        const res = await apiClient.post('/purchase/getPurchase', { id }, { offlinePolicy: 'read' });
        if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
        const data = res?.data?.data ?? res?.data;
        if (data) {
            await savePurchaseDetail(data, cacheEpoch);
            return detailResult(data, 'network');
        }
    } catch (err) {
        if (isStaleOfflineCacheWrite(err) || !isOfflineCacheEpochCurrent(cacheEpoch)) {
            return detailResult(null, 'none');
        }
        console.warn('[fetchPurchaseDetail] network failed', err);
        if (isAuthoritativeDetailDenial(err)) {
            await deletePurchaseDetail(id);
            return detailResult(null, 'none');
        }
    }
    if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
    const cached = await getPurchaseDetail(id);
    return cached ? detailResult(cached, 'cache') : detailResult(null, 'none');
};

export const saveSupplierDetail = (payload, expectedEpoch) => saveDomainDetail('supplier_details', payload?.id, payload, expectedEpoch);
export const getSupplierDetail = (id) => getDomainDetail('supplier_details', id);
export const deleteSupplierDetail = async (id) => {
    if (offlineDB.supplier_details) await offlineDB.supplier_details.delete(Number(id) || id);
};

export const fetchSupplierDetail = async (id, { forceRefresh = false } = {}) => {
    if (!id) return detailResult(null, 'none');
    if (!isConfirmedOnline()) {
        const cached = await getSupplierDetail(id);
        return cached ? detailResult(cached, 'cache') : detailResult(null, 'none');
    }
    const cacheEpoch = captureOfflineCacheEpoch();
    try {
        const res = await apiClient.post('/supplier/getSupplierById', { id }, { offlinePolicy: 'read' });
        if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
        const data = res?.data?.data ?? res?.data;
        if (data) {
            const lean = { ...data };
            delete lean.payable_balance;
            delete lean.advance_balance;
            await saveSupplierDetail(lean, cacheEpoch);
            return detailResult(data, 'network');
        }
    } catch (err) {
        if (isStaleOfflineCacheWrite(err) || !isOfflineCacheEpochCurrent(cacheEpoch)) {
            return detailResult(null, 'none');
        }
        console.warn('[fetchSupplierDetail] network failed', err);
        if (isAuthoritativeDetailDenial(err)) {
            await deleteSupplierDetail(id);
            return detailResult(null, 'none');
        }
    }
    if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
    const cached = await getSupplierDetail(id);
    return cached ? detailResult(cached, 'cache', null, ['payable_balance', 'advance_balance']) : detailResult(null, 'none');
};

export const saveCustomerDetail = (payload, expectedEpoch) => saveDomainDetail('customer_details', payload?.id, payload, expectedEpoch);
export const getCustomerDetail = (id) => getDomainDetail('customer_details', id);
export const deleteCustomerDetail = async (id) => {
    if (offlineDB.customer_details) await offlineDB.customer_details.delete(Number(id) || id);
};

export const fetchCustomerDetail = async (id, { forceRefresh = false } = {}) => {
    if (!id) return detailResult(null, 'none');
    if (!isConfirmedOnline()) {
        const cached = await getCustomerDetail(id);
        return cached ? detailResult(cached, 'cache') : detailResult(null, 'none');
    }
    const cacheEpoch = captureOfflineCacheEpoch();
    try {
        const res = await apiClient.get(`/customer/show/${id}`, { offlinePolicy: 'read' });
        if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
        const data = res?.data?.customer ?? res?.data?.data ?? res?.data;
        if (data) {
            const lean = { ...data };
            delete lean.wallet_transactions;
            await saveCustomerDetail(lean, cacheEpoch);
            if (offlineDB.customers && lean.id) {
                assertOfflineCacheEpoch(cacheEpoch);
                await offlineDB.customers.put({
                    id: lean.id,
                    name: lean.name ?? '',
                    phone: lean.phone ?? null,
                    email: lean.email ?? null,
                    internal_code: lean.internal_code ?? null,
                    type: lean.type ?? lean.customer_type ?? 'normal',
                    wholesaler_id: lean.wholesaler_id ?? null,
                    company_name: lean.company_name ?? null,
                    status: lean.status ?? null,
                    _cached_at: new Date().toISOString(),
                    updated_at: lean.updated_at ?? null,
                });
            }
            return detailResult(data, 'network');
        }
    } catch (err) {
        if (isStaleOfflineCacheWrite(err) || !isOfflineCacheEpochCurrent(cacheEpoch)) {
            return detailResult(null, 'none');
        }
        console.warn('[fetchCustomerDetail] network failed', err);
        if (isAuthoritativeDetailDenial(err)) {
            await deleteCustomerDetail(id);
            return detailResult(null, 'none');
        }
    }
    if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
    const cached = await getCustomerDetail(id);
    return cached ? detailResult(cached, 'cache', null, ['wallet_transactions']) : detailResult(null, 'none');
};

// ------------------------------------------------------------------
// Automotives
// ------------------------------------------------------------------

export const saveAutomotiveDetail = (payload, expectedEpoch) => saveDomainDetail('automotive_details', payload?.id, payload, expectedEpoch);
export const getAutomotiveDetail = (id) => getDomainDetail('automotive_details', id);
export const deleteAutomotiveDetail = async (id) => {
    if (offlineDB.automotive_details) await offlineDB.automotive_details.delete(Number(id) || id);
};

export const fetchAutomotiveDetail = async (id, { forceRefresh = false } = {}) => {
    if (!id) return detailResult(null, 'none');
    if (!isConfirmedOnline()) {
        const cached = await getAutomotiveDetail(id);
        return cached
            ? detailResult(cached, 'cache', null, ['sales', 'service_history', 'activity_log', 'documents', 'stats'])
            : detailResult(null, 'none');
    }
    const cacheEpoch = captureOfflineCacheEpoch();
    try {
        const res = await apiClient.post(`/automotive/getAutomotive/${id}`, {}, { offlinePolicy: 'read' });
        if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
        const data = res?.data?.automotive ?? res?.data?.data ?? res?.data;
        if (data) {
            const lean = { ...data };
            // Unbounded or staleness-sensitive sections are never cached: history
            // grows without limit, money figures go stale, and document links are
            // blobs the storage-quota budget cannot carry.
            delete lean.sales;
            delete lean.service_history;
            delete lean.activity_log;
            delete lean.attachments;
            delete lean.stats;
            await saveAutomotiveDetail(lean, cacheEpoch);
            if (offlineDB.automotives && lean.id) {
                assertOfflineCacheEpoch(cacheEpoch);
                await offlineDB.automotives.put({
                    id: lean.id,
                    // Number(), not the raw value: Dexie is type-strict and a
                    // string here makes the row invisible to the POS picker.
                    customer_id: lean.customer_id != null ? Number(lean.customer_id) : null,
                    registration_number: lean.registration_number ?? null,
                    serial_number: lean.serial_number ?? null,
                    display_name: lean.display_name ?? null,
                    name: lean.name ?? null,
                    category: lean.category ?? null,
                    manufacturer: lean.manufacturer ?? null,
                    model: lean.model ?? null,
                    odometer: lean.odometer ?? null,
                    odometer_unit: lean.odometer_unit ?? 'km',
                    tracks_distance: Boolean(lean.tracks_distance),
                    is_archived: Number(lean.is_archived ?? 0),
                    service_status: lean.service_status ?? 'not_tracked',
                    expiry_status: lean.expiry_status ?? 'none',
                    next_service_due_at: lean.next_service_due_at ?? null,
                    next_expiry_at: lean.next_expiry_at ?? null,
                    last_service_at: lean.last_service_at ?? null,
                    _cached_at: new Date().toISOString(),
                    updated_at: lean.updated_at ?? null,
                });
            }
            return detailResult(data, 'network');
        }
    } catch (err) {
        if (isStaleOfflineCacheWrite(err) || !isOfflineCacheEpochCurrent(cacheEpoch)) {
            return detailResult(null, 'none');
        }
        console.warn('[fetchAutomotiveDetail] network failed', err);
        if (isAuthoritativeDetailDenial(err)) {
            await deleteAutomotiveDetail(id);
            return detailResult(null, 'none');
        }
    }
    if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
    const cached = await getAutomotiveDetail(id);
    return cached
        ? detailResult(cached, 'cache', null, ['sales', 'service_history', 'activity_log', 'documents', 'stats'])
        : detailResult(null, 'none');
};

/**
 * Automotives owned by one customer, active only.
 *
 * Online-first with a Dexie fallback, the same shape as lookupCustomersByPhone,
 * so the POS picker keeps working offline. Two consumers: the POS picker and the
 * Automotives tab on a customer's profile.
 */
export const fetchCustomerAutomotives = async (customerId) => {
    const ownerId = Number(customerId);
    if (!Number.isSafeInteger(ownerId) || ownerId <= 0) return { items: [], source: 'none' };

    const readCache = async () => {
        if (!offlineDB.automotives) return [];
        const rows = await offlineDB.automotives.where('customer_id').equals(ownerId).toArray();
        return rows.filter((row) => !Number(row.is_archived));
    };

    if (!isConfirmedOnline()) {
        return { items: await readCache(), source: 'cache' };
    }

    try {
        const res = await apiClient.post('/automotive/fetchCustomerAutomotives', { customer_id: ownerId }, { offlinePolicy: 'read' });
        return { items: res?.data?.data ?? [], source: 'network' };
    } catch (err) {
        console.warn('[fetchCustomerAutomotives] network failed', err);
        return { items: await readCache(), source: 'cache' };
    }
};

// ------------------------------------------------------------------
// Sales / Transfers / Gate passes / Order requests
// ------------------------------------------------------------------

export const saveSaleViewDetail = (payload, expectedEpoch) =>
    saveDomainDetail('sales_details', payload?.id ?? payload?.sale?.id, payload, expectedEpoch);
export const getSaleViewDetail = (id) => getDomainDetail('sales_details', id);
export const deleteSaleViewDetail = async (id) => {
    if (offlineDB.sales_details) await offlineDB.sales_details.delete(Number(id) || id);
};

export const fetchSaleViewDetail = async (id, { forceRefresh = false } = {}) => {
    if (!id) return detailResult(null, 'none');
    if (!isConfirmedOnline()) {
        const cached = await getSaleViewDetail(id);
        return cached ? detailResult(cached, 'cache') : detailResult(null, 'none');
    }
    const cacheEpoch = captureOfflineCacheEpoch();
    try {
        const res = await apiClient.get(`/sale/${id}`, { offlinePolicy: 'read' });
        if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
        const data = res?.data?.data ?? res?.data;
        if (data) {
            const saleId = data.sale?.id ?? data.id ?? id;
            await saveSaleViewDetail({ ...data, id: saleId }, cacheEpoch);
            if (offlineDB.sales && data.sale) {
                assertOfflineCacheEpoch(cacheEpoch);
                const s = data.sale;
                await offlineDB.sales.put({
                    id: s.id,
                    sales_order: s.sales_order ?? null,
                    status: s.status ?? null,
                    payment_status: s.payment_status ?? null,
                    date: s.date ?? null,
                    branch_id: s.branch_id ?? null,
                    payment_branch_id: s.payment_branch_id ?? null,
                    customer_id: s.customer_id ?? data.customer?.id ?? null,
                    customer_name: data.customer?.name ?? null,
                    final_total: data.summary?.final_total ?? s.final_total ?? null,
                    paid_amount: data.summary?.paid_amount ?? s.paid_amount ?? null,
                    refunded_amount: data.summary?.refunded_amount ?? s.refunded_amount ?? 0,
                    created_at: s.created_at ?? null,
                    updated_at: s.updated_at ?? null,
                    _cached_at: new Date().toISOString(),
                });
            }
            return detailResult({ ...data, id: saleId }, 'network');
        }
    } catch (err) {
        if (isStaleOfflineCacheWrite(err) || !isOfflineCacheEpochCurrent(cacheEpoch)) {
            return detailResult(null, 'none');
        }
        console.warn('[fetchSaleViewDetail] network failed', err);
        if (isAuthoritativeDetailDenial(err)) {
            await deleteSaleViewDetail(id);
            return detailResult(null, 'none');
        }
    }
    if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
    const cached = await getSaleViewDetail(id);
    return cached ? detailResult(cached, 'cache') : detailResult(null, 'none');
};

export const saveTransferDetail = (payload, expectedEpoch) => saveDomainDetail('transfer_details', payload?.id, payload, expectedEpoch);
export const getTransferDetail = (id) => getDomainDetail('transfer_details', id);
export const deleteTransferDetail = async (id) => {
    if (offlineDB.transfer_details) await offlineDB.transfer_details.delete(Number(id) || id);
};

export const fetchTransferDetail = async (id, { forceRefresh = false } = {}) => {
    if (!id) return detailResult(null, 'none');
    if (!isConfirmedOnline()) {
        const cached = await getTransferDetail(id);
        return cached ? detailResult(cached, 'cache') : detailResult(null, 'none');
    }
    const cacheEpoch = captureOfflineCacheEpoch();
    try {
        const res = await apiClient.get(`/transfer/${id}`, { offlinePolicy: 'read' });
        if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
        const data = res?.data?.data ?? res?.data;
        if (data?.id) {
            await saveTransferDetail(data, cacheEpoch);
            if (offlineDB.transfers) {
                assertOfflineCacheEpoch(cacheEpoch);
                await offlineDB.transfers.put({
                    id: data.id,
                    transfer_number: data.transfer_number ?? null,
                    status: data.status ?? null,
                    from_branch_id: data.from_branch_id ?? data.from_branch?.id ?? null,
                    to_branch_id: data.to_branch_id ?? data.to_branch?.id ?? null,
                    from_branch_name: data.from_branch?.name ?? data.fromBranch?.name ?? null,
                    to_branch_name: data.to_branch?.name ?? data.toBranch?.name ?? null,
                    created_at: data.created_at ?? null,
                    updated_at: data.updated_at ?? null,
                    _cached_at: new Date().toISOString(),
                });
            }
            return detailResult(data, 'network');
        }
    } catch (err) {
        if (isStaleOfflineCacheWrite(err) || !isOfflineCacheEpochCurrent(cacheEpoch)) {
            return detailResult(null, 'none');
        }
        console.warn('[fetchTransferDetail] network failed', err);
        if (isAuthoritativeDetailDenial(err)) {
            await deleteTransferDetail(id);
            return detailResult(null, 'none');
        }
    }
    if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
    const cached = await getTransferDetail(id);
    return cached ? detailResult(cached, 'cache') : detailResult(null, 'none');
};

export const saveGatePassDetail = (payload, expectedEpoch = captureOfflineCacheEpoch()) =>
    saveDomainDetail(
        'gate_pass_details',
        payload?.id,
        {
            ...payload,
            gatepass_no: payload?.gatepass_no ?? null,
        },
        expectedEpoch,
    );
export const getGatePassDetail = async (idOrNo) => {
    if (!offlineDB.gate_pass_details || idOrNo == null) return null;
    const byId = await offlineDB.gate_pass_details.get(Number(idOrNo) || idOrNo);
    if (byId) return byId;
    return offlineDB.gate_pass_details.where('gatepass_no').equals(String(idOrNo)).first();
};
export const deleteGatePassDetail = async (id) => {
    if (offlineDB.gate_pass_details) await offlineDB.gate_pass_details.delete(Number(id) || id);
};

export const saveLoadFormDetail = (payload, expectedEpoch = captureOfflineCacheEpoch()) =>
    saveDomainDetail(
        'load_form_details',
        payload?.id,
        {
            ...payload,
            load_form_no: payload?.load_form_no ?? null,
        },
        expectedEpoch,
    );

export const getLoadFormDetail = async (idOrNo) => {
    if (!offlineDB.load_form_details || idOrNo == null) return null;
    const byId = await offlineDB.load_form_details.get(Number(idOrNo) || idOrNo);
    if (byId) return byId;
    return offlineDB.load_form_details.where('load_form_no').equals(String(idOrNo)).first();
};

export const deleteLoadFormDetail = async (id) => {
    if (offlineDB.load_form_details) await offlineDB.load_form_details.delete(Number(id) || id);
};

/**
 * The load form detail endpoint returns the whole run, so unlike gate passes
 * this reads it directly by id rather than re-filtering the list.
 */
export const fetchLoadFormDetail = async (id, { forceRefresh = false } = {}) => {
    if (id == null) return detailResult(null, 'none');

    if (!isConfirmedOnline()) {
        const cached = await getLoadFormDetail(id);
        return cached ? detailResult(cached, 'cache') : detailResult(null, 'none');
    }

    if (!forceRefresh) {
        const cached = await getLoadFormDetail(id);
        if (cached) {
            // Still refresh in the background; a dispatched run changes often.
            fetchLoadFormDetail(id, { forceRefresh: true }).catch(() => {});
            return detailResult(cached, 'cache');
        }
    }

    const cacheEpoch = captureOfflineCacheEpoch();
    try {
        const res = await apiClient.get(`load-form/${id}`, { offlinePolicy: 'read' });
        if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');

        const data = res?.data?.data ?? null;
        if (data) {
            await saveLoadFormDetail(data, cacheEpoch);
            return detailResult(data, 'network');
        }
    } catch (err) {
        console.warn('[fetchLoadFormDetail] network failed', err);
        if (isAuthoritativeDetailDenial(err)) {
            const stale = await getLoadFormDetail(id);
            if (stale?.id != null) await deleteLoadFormDetail(stale.id);
            return detailResult(null, 'none');
        }
    }

    if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
    const cached = await getLoadFormDetail(id);
    return cached ? detailResult(cached, 'cache') : detailResult(null, 'none');
};

export const fetchGatePassDetail = async (gatepassNo, { forceRefresh = false } = {}) => {
    if (!gatepassNo) return detailResult(null, 'none');
    if (!isConfirmedOnline()) {
        const cached = await getGatePassDetail(gatepassNo);
        return cached ? detailResult(cached, 'cache') : detailResult(null, 'none');
    }
    const cacheEpoch = captureOfflineCacheEpoch();
    try {
        const res = await apiClient.post(
            'gatepass/fetchGatePassList',
            { search: gatepassNo, per_page: 1, perPage: 1, page: 1 },
            { offlinePolicy: 'read' },
        );
        if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
        const page = res?.data?.data;
        const rows = page?.data ?? (Array.isArray(page) ? page : []);
        const data = rows.find((row) => String(row?.gatepass_no) === String(gatepassNo)) ?? null;
        if (data) {
            await saveGatePassDetail(data, cacheEpoch);
            if (offlineDB.gate_passes && data.id) {
                assertOfflineCacheEpoch(cacheEpoch);
                await offlineDB.gate_passes.put({
                    id: data.id,
                    gatepass_no: data.gatepass_no ?? null,
                    status: data.status ?? null,
                    date: data.date ?? null,
                    sale_id: data.sale_id ?? null,
                    transfer_id: data.transfer_id ?? null,
                    updated_at: data.updated_at ?? null,
                    _cached_at: new Date().toISOString(),
                });
            }
            return detailResult(data, 'network');
        }
        const stale = await getGatePassDetail(gatepassNo);
        if (stale?.id != null) await deleteGatePassDetail(stale.id);
        return detailResult(null, 'none');
    } catch (err) {
        if (isStaleOfflineCacheWrite(err) || !isOfflineCacheEpochCurrent(cacheEpoch)) {
            return detailResult(null, 'none');
        }
        console.warn('[fetchGatePassDetail] network failed', err);
        if (isAuthoritativeDetailDenial(err)) {
            const stale = await getGatePassDetail(gatepassNo);
            if (stale?.id != null) await deleteGatePassDetail(stale.id);
            return detailResult(null, 'none');
        }
    }
    if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
    const cached = await getGatePassDetail(gatepassNo);
    return cached ? detailResult(cached, 'cache') : detailResult(null, 'none');
};

export const saveOrderRequestDetail = (payload, expectedEpoch) => saveDomainDetail('order_request_details', payload?.id, payload, expectedEpoch);
export const getOrderRequestDetail = (id) => getDomainDetail('order_request_details', id);
export const deleteOrderRequestDetail = async (id) => {
    if (offlineDB.order_request_details) await offlineDB.order_request_details.delete(Number(id) || id);
};

export const fetchOrderRequestDetail = async (id, { forceRefresh = false } = {}) => {
    if (!id) return detailResult(null, 'none');
    if (!isConfirmedOnline()) {
        const cached = await getOrderRequestDetail(id);
        return cached ? detailResult(cached, 'cache') : detailResult(null, 'none');
    }
    const cacheEpoch = captureOfflineCacheEpoch();
    try {
        const res = await apiClient.get(`/wholesaler-order-request/${id}`, { offlinePolicy: 'read' });
        if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
        const data = res?.data?.data ?? res?.data;
        if (data?.id) {
            await saveOrderRequestDetail(data, cacheEpoch);
            if (offlineDB.wholesaler_order_requests) {
                assertOfflineCacheEpoch(cacheEpoch);
                await offlineDB.wholesaler_order_requests.put({
                    id: data.id,
                    order_no: data.order_no ?? null,
                    status: data.status ?? null,
                    wholesaler_id: data.wholesaler_id ?? data.wholesaler?.id ?? null,
                    wholesaler_name: data.wholesaler?.name ?? data.wholesaler_name ?? null,
                    sale_id: data.sale_id ?? null,
                    total_amount: data.total_amount ?? null,
                    created_at: data.created_at ?? null,
                    updated_at: data.updated_at ?? null,
                    _cached_at: new Date().toISOString(),
                });
            }
            return detailResult(data, 'network');
        }
    } catch (err) {
        if (isStaleOfflineCacheWrite(err) || !isOfflineCacheEpochCurrent(cacheEpoch)) {
            return detailResult(null, 'none');
        }
        console.warn('[fetchOrderRequestDetail] network failed', err);
        if (isAuthoritativeDetailDenial(err)) {
            await deleteOrderRequestDetail(id);
            return detailResult(null, 'none');
        }
    }
    if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
    const cached = await getOrderRequestDetail(id);
    return cached ? detailResult(cached, 'cache') : detailResult(null, 'none');
};

// ------------------------------------------------------------------
// Expenses / Accounts
// ------------------------------------------------------------------

export const saveExpenseDetail = (payload, expectedEpoch) => saveDomainDetail('expense_details', payload?.id, payload, expectedEpoch);
export const getExpenseDetail = (id) => getDomainDetail('expense_details', id);
export const deleteExpenseDetail = async (id) => {
    if (offlineDB.expense_details) await offlineDB.expense_details.delete(Number(id) || id);
};

const stripExpenseAttachmentsForCache = (data) => {
    if (!data || typeof data !== 'object') return data;
    const lean = { ...data };
    if (Array.isArray(lean.attachments)) {
        lean.attachments = lean.attachments.map((att) => {
            if (!att || typeof att !== 'object') return att;
            const { file_content, content, binary, ...meta } = att;
            return meta;
        });
    }
    return lean;
};

export const fetchExpenseDetail = async (id, { forceRefresh = false } = {}) => {
    if (!id) return detailResult(null, 'none');
    if (!isConfirmedOnline()) {
        const cached = await getExpenseDetail(id);
        return cached ? detailResult(cached, 'cache') : detailResult(null, 'none');
    }
    const cacheEpoch = captureOfflineCacheEpoch();
    try {
        const res = await apiClient.post('/expense/fetchExpense', { id }, { offlinePolicy: 'read' });
        if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
        const data = res?.data?.data ?? res?.data;
        if (data?.id) {
            const lean = stripExpenseAttachmentsForCache(data);
            await saveExpenseDetail(lean, cacheEpoch);
            if (offlineDB.expenses) {
                assertOfflineCacheEpoch(cacheEpoch);
                await offlineDB.expenses.put({
                    id: lean.id,
                    expense_number: lean.expense_number ?? null,
                    title: lean.title ?? null,
                    description: lean.description ?? null,
                    amount: lean.amount ?? null,
                    date: lean.date ?? null,
                    payment_method: lean.payment_method ?? null,
                    branch_id: lean.branch_id ?? lean.branch?.id ?? null,
                    category_id: lean.category_id ?? lean.category?.id ?? null,
                    created_at: lean.created_at ?? null,
                    updated_at: lean.updated_at ?? null,
                    _cached_at: new Date().toISOString(),
                });
            }
            return detailResult(lean, 'network');
        }
    } catch (err) {
        if (isStaleOfflineCacheWrite(err) || !isOfflineCacheEpochCurrent(cacheEpoch)) {
            return detailResult(null, 'none');
        }
        console.warn('[fetchExpenseDetail] network failed', err);
        if (isAuthoritativeDetailDenial(err)) {
            await deleteExpenseDetail(id);
            return detailResult(null, 'none');
        }
    }
    if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
    const cached = await getExpenseDetail(id);
    return cached ? detailResult(cached, 'cache') : detailResult(null, 'none');
};

export const saveAccountDetail = (payload, expectedEpoch) => saveDomainDetail('account_details', payload?.id, payload, expectedEpoch);
export const getAccountDetail = (id) => getDomainDetail('account_details', id);
export const deleteAccountDetail = async (id) => {
    if (offlineDB.account_details) await offlineDB.account_details.delete(Number(id) || id);
};

const leanAccountSnapshot = (data) => {
    if (!data || typeof data !== 'object') return null;
    const cachedAt = new Date().toISOString();
    return {
        id: data.id,
        account_title: data.account_title ?? null,
        account_number: data.account_number ?? null,
        bank_name: data.bank_name ?? null,
        country: data.country ?? null,
        status: data.status ?? null,
        use_for_sales: data.use_for_sales ?? false,
        use_for_purchases: data.use_for_purchases ?? false,
        use_for_refunds: data.use_for_refunds ?? false,
        use_for_expenses: data.use_for_expenses ?? false,
        current_balance: data.current_balance ?? null,
        updated_at: data.updated_at ?? null,
        _cached_at: cachedAt,
    };
};

export const fetchAccountDetail = async (id, { forceRefresh = false } = {}) => {
    if (!id) return detailResult(null, 'none');
    if (!isConfirmedOnline()) {
        const cached = await getAccountDetail(id);
        return cached ? detailResult(cached, 'cache', null, ['ledger', 'current_balance']) : detailResult(null, 'none');
    }
    const cacheEpoch = captureOfflineCacheEpoch();
    try {
        const res = await apiClient.post('/account/fetchAccount', { id }, { offlinePolicy: 'read' });
        if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
        const data = res?.data?.data ?? res?.data;
        if (data?.id) {
            const lean = leanAccountSnapshot(data);
            // Never persist ledger transactions.
            delete lean.transactions;
            delete lean.ledger;
            await saveAccountDetail(lean, cacheEpoch);
            if (offlineDB.accounts) {
                assertOfflineCacheEpoch(cacheEpoch);
                await offlineDB.accounts.put({ ...lean });
            }
            return detailResult(lean, 'network');
        }
    } catch (err) {
        if (isStaleOfflineCacheWrite(err) || !isOfflineCacheEpochCurrent(cacheEpoch)) {
            return detailResult(null, 'none');
        }
        console.warn('[fetchAccountDetail] network failed', err);
        if (isAuthoritativeDetailDenial(err)) {
            await deleteAccountDetail(id);
            return detailResult(null, 'none');
        }
    }
    if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
    const cached = await getAccountDetail(id);
    return cached ? detailResult(cached, 'cache', null, ['ledger', 'current_balance']) : detailResult(null, 'none');
};

// ------------------------------------------------------------------
// Delivery people
// ------------------------------------------------------------------

export const saveDeliveryPersonDetail = (payload, expectedEpoch) => saveDomainDetail('delivery_person_details', payload?.id, payload, expectedEpoch);
export const getDeliveryPersonDetail = (id) => getDomainDetail('delivery_person_details', id);
export const deleteDeliveryPersonDetail = async (id) => {
    if (offlineDB.delivery_person_details) await offlineDB.delivery_person_details.delete(Number(id) || id);
};

/**
 * The lean list row, shaped exactly as syncDeliveryPersons writes it so the
 * detail visit and the list sync cannot disagree about a row's columns.
 */
const leanDeliveryPersonRow = (person) => ({
    id: person.id,
    name: person.name ?? null,
    phone: person.phone ?? null,
    cnic: person.cnic ?? null,
    licence_no: person.licence_no ?? null,
    licence_expiry: person.licence_expiry ?? null,
    vehicle_no: person.vehicle_no ?? null,
    // Number(), not the raw value: Dexie is type-strict and a string here is
    // invisible to .where('branch_id').equals(5).
    branch_id: person.branch_id != null ? Number(person.branch_id) : null,
    branch_name: person.branch_name ?? null,
    // 1/0, never true/false — the offline DataTable's exact filter compares the
    // stringified row value against the literal '1'.
    is_active: Number(person.is_active ?? 0),
    note: person.note ?? null,
    created_at: person.created_at ?? null,
    updated_at: person.updated_at ?? null,
    _cached_at: new Date().toISOString(),
});

/**
 * Never cached. The counts are branch-scoped server aggregates that go stale the
 * moment a load form is dispatched, and a stale "0 open" reads as fact rather
 * than as a cached figure; both tabs are paginated document lists whose own
 * branch scoping is resolved server-side per request.
 */
const DELIVERY_PERSON_ONLINE_SECTIONS = ['summary', 'load_forms', 'gate_passes'];

export const fetchDeliveryPersonDetail = async (id, { forceRefresh = false } = {}) => {
    if (!id) return detailResult(null, 'none');
    if (!isConfirmedOnline()) {
        const cached = await getDeliveryPersonDetail(id);
        return cached ? detailResult(cached, 'cache', null, DELIVERY_PERSON_ONLINE_SECTIONS) : detailResult(null, 'none');
    }
    const cacheEpoch = captureOfflineCacheEpoch();
    try {
        const res = await apiClient.get(`/delivery-person/${id}`, { offlinePolicy: 'read' });
        if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
        const data = res?.data?.data ?? {};
        const person = data.person ?? null;
        if (person?.id) {
            const tabs = data.tabs ?? {};
            await saveDeliveryPersonDetail({ id: person.id, person, tabs }, cacheEpoch);
            if (offlineDB.delivery_persons) {
                assertOfflineCacheEpoch(cacheEpoch);
                // Merged over the existing row: the detail payload carries no
                // created_at/updated_at, and a blind put would blank the columns
                // the list sync had already cached.
                const existing = (await offlineDB.delivery_persons.get(Number(person.id))) || {};
                await offlineDB.delivery_persons.put(leanDeliveryPersonRow({ ...existing, ...person }));
            }
            return detailResult({ id: person.id, person, summary: data.summary ?? {}, tabs }, 'network');
        }
    } catch (err) {
        if (isStaleOfflineCacheWrite(err) || !isOfflineCacheEpochCurrent(cacheEpoch)) {
            return detailResult(null, 'none');
        }
        console.warn('[fetchDeliveryPersonDetail] network failed', err);
        if (isAuthoritativeDetailDenial(err)) {
            await deleteDeliveryPersonDetail(id);
            return detailResult(null, 'none');
        }
    }
    if (!isOfflineCacheEpochCurrent(cacheEpoch)) return detailResult(null, 'none');
    const cached = await getDeliveryPersonDetail(id);
    return cached ? detailResult(cached, 'cache', null, DELIVERY_PERSON_ONLINE_SECTIONS) : detailResult(null, 'none');
};

export const clearAllIndexedDB = async () => {
    invalidateOfflineCacheWrites();
    await offlineDB.transaction('rw', offlineDB.tables, async () => {
        for (const table of offlineDB.tables) {
            await table.clear();
        }
    });
};

export default offlineDB;
